<script setup>
    import { onScopeDispose } from 'vue';
    import { useDebounceFn, useMediaQuery } from '@vueuse/core';
    import { useRouteQuery } from '@vueuse/router';
    import { useHeroAltLogo } from '@/composables/useHeroAltLogo';
    import { useMyEventOpener } from '@/composables/useMyEventOpener';
    import { getRoleCopy } from '@/composables/builder/getRoleCopy';
    import { date_options_2026, date_options_cal_2026, getConferenceTimezone, getPartnerDay, skill_level_options, seating_options, availability_options, MY_EVENT_TYPE } from '~~/shared/utilities/schedule';
    import { agendaWindowHours } from '~~/shared/utilities/schedule/day-window';
    import { canAccessPartnerDay } from '~~/shared/utilities/schedule/ticket';
    import { deriveUserAdded, deriveSeatState } from '#shared/utilities/schedule/user-added';
    import { normalizeEventForCard } from '#shared/utilities/schedule/normalize-event';
    import { overlapsTimeRange } from '#shared/utilities/schedule/time-range';
    import MyEventModal from '~/components/fragments/dialogs/my-event.vue';
    import AddToCalendarModal from '~/components/fragments/dialogs/add-to-calendar.vue';
    import ShareScheduleModal from '~/components/fragments/dialogs/share-schedule.vue';
    import FindASessionModal from '~/components/fragments/dialogs/find-a-session.vue';
    import ScheduleBuilderBanner from '~/components/blocks/two-six/agenda/schedule-builder-banner.vue';

    import AgendaDetailList from '~/components/blocks/two-six/agenda/detail-list.vue';
    import AgendaOverviewList from '~/components/blocks/two-six/agenda/overview-list.vue';
    import AgendaCalendarList from '~/components/blocks/two-six/agenda/calendar-list.vue';
    import ScheduleEmptyState from '~/components/blocks/two-six/agenda/schedule-empty-state.vue';

    // Dark "alt" header logo — only when this block is index 0 (the page hero).
    const rootRef = ref(null);
    useHeroAltLogo(rootRef);

    const { loggedIn, user, fetch: refreshSession } = useUserSession();

    // On My Schedule, show only sessions still on the user's schedule. The feed
    // can lag a just-removed session (its refetch is async), so this client
    // filter owns visibility — a removed session drops immediately once
    // user.scheduled updates (via the card's refreshSession()).
    const filterToScheduled = (sessions) => {
        if (tab.value !== 'myschedule' || !sessions) return sessions;
        const scheduled = user.value?.scheduled || [];
        return sessions.filter((s) => scheduled.includes(s.unique_id));
    };

    const myEvents = useMyEvents();
    const { open: openModal } = useModal();

    // Load the user's custom events whenever we're logged in (they only surface on
    // the My Schedule tab, but loading is cheap and keeps the list warm).
    watch(() => user.value?.contactId, (id) => { if (id) myEvents.loadEvents(); }, { immediate: true });

    const normalizedEvents = computed(() =>
        (myEvents.events.value || []).map(normalizeEventForCard)
    );

    // Open Create/Edit, then refresh the feed + event list on a real change.
    const openMyEvent = async (rawEvent = null) => {
        const result = await openModal(MyEventModal, { mode: rawEvent ? 'edit' : 'create', event: rawEvent });
        if (result?.changed) {
            await myEvents.loadEvents();
            // Editing or deleting an event can create or clear a clash, and this
            // path emits no `schedule:changed` — nothing else would refresh it.
            await refreshConflictIds();
            refresh(dataKey);
        }
    };
    provide('openMyEvent', openMyEvent);

    // provide/inject only reaches the agenda's own subtree. Dialogs mount at the
    // app root, so the double-book modal reaches the editor through this
    // module-scoped bridge instead. Unregistered on unmount so a stale opener
    // can't outlive this block.
    const { registerMyEventOpener } = useMyEventOpener();
    const unregisterMyEventOpener = registerMyEventOpener(openMyEvent);
    onScopeDispose(unregisterMyEventOpener);

    // Open the Schedule Builder wizard.
    // Resetting builder state, counting the interaction (the promo banner retires
    // after three), and re-hydrating the session afterwards all live in
    // useScheduleBuilderLauncher — the nav's /build-schedule deep link runs the
    // builder too, and must do all of it identically. What's left here is the part
    // only the agenda can do: refresh its own feed, via schedule:built below.
    const scheduleBuilder = useScheduleBuilder();
    const { launchScheduleBuilder } = useScheduleBuilderLauncher();
    onMounted(() => scheduleBuilder.loadInteractions());

    const openScheduleBuilder = async () => {
        if (!showMySchedule.value) return;
        await launchScheduleBuilder();
    };

    // Action-bar Schedule Preferences → the edit drawer (Phase 1).
    const openSchedulePreferences = () => { showPrefsDrawer.value = true; };

    // Action-bar Add to My Calendar (Phase 2) — pick a platform and subscribe
    // My Schedule as a live ICS feed (server/api/v1/schedule/calendar/**).
    const openAddToCalendar = async () => { await openModal(AddToCalendarModal, {}); };

    // Share My Schedule (Phase 3) — mints a read-only share link via
    // GET /api/v1/schedule/calendar/links (`share`) and hands it to the
    // FragmentsShare social/copy-link row.
    const openShareSchedule = async () => { await openModal(ShareScheduleModal, {}); };

    // Open Find a Session (spec decisions 1, 6). find-a-session.vue now owns its
    // own 'prefs' gate view (cleanup pass — replaces the Task 13 implementer's
    // inline hand-rolled PrefsGateModal that used to live here): this function
    // only looks up whether prefs are already saved and passes that down via
    // `needsPrefs`, letting the modal itself decide whether to show the gate
    // before firing the suggest request. After the real modal resolves,
    // refreshSession() then refetchSchedule() — same ordering/pattern as
    // openMyEvent/openScheduleBuilder above — so any session added while the
    // modal was open appears on the calendar. `View Agenda` (the No Results
    // screen) hands back `{ viewAgenda: true, gap }`, routed to
    // viewAgendaForGap (below).
    // The session cookie's `scheduled` list can drift from D1: a plain
    // refreshSession() reads the SEALED COOKIE, not the database, so a schedule
    // changed on another device/tab (or by an admin) stays stale here until a
    // hydrate. On the My Schedule tab that staleness is user-visible AND it
    // breaks Find a Session — the calendar draws gap blocks from the (stale)
    // cookie, but the suggest endpoint validates the clicked gap against D1 and
    // rejects the mismatch with 400 ("That time slot is not open") → the modal's
    // generic error. Re-hydrate from D1 whenever My Schedule becomes active so
    // the client's schedule — and therefore its gaps — match what the server
    // sees. Best-effort: a failed hydrate just leaves the existing cookie.
    // (Declared before `tab`; the tab watcher that drives it lives after `tab`.)
    const syncScheduleFromD1 = async () => {
        if (!loggedIn.value) return;
        try {
            await $fetch('/api/v1/schedule/hydrate', { method: 'POST' });
            await refreshSession();
        } catch (e) { /* leave the existing cookie on a transient failure */ }
    };

    const openFindASession = async (mode, gap) => {
        let prefsText = '';
        try {
            const res = await $fetch('/api/v1/schedule/preferences');
            prefsText = res?.prefs || '';
        } catch (e) {
            // Treat a failed lookup the same as "no prefs yet" — never block the
            // user from at least attempting Find a Session over a transient error.
            prefsText = '';
        }

        const result = await openModal(FindASessionModal, { mode, gap, needsPrefs: !prefsText?.trim() });

        // Re-sync from D1 (not just refreshSession, which reads the sealed
        // cookie) so the gap blocks reflect the current schedule after the modal
        // — a session added inside it, or a gap that had gone stale and 400'd,
        // is corrected on the next render.
        await syncScheduleFromD1();
        await refetchSchedule();

        if (result?.viewAgenda) {
            viewAgendaForGap(result.gap);
        }
    };

    // Mobile vs desktop split for the controls bar. Below `lg` we show:
    //   - tab toggle as a dropdown (USelect) instead of the two-button row
    //   - icon-only view pills
    //   - search button in row 2, day/time-range/roles selects hidden (drawer-only)
    const isMobile = useMediaQuery('(max-width: 1023px)', { ssrWidth: 768 });
    // Narrow phone breakpoint — the mobile controls collapse from 2 rows to a
    // 3-row stack (tab, view-pills + Filters, search + results) below this width.
    const isNarrow = useMediaQuery('(max-width: 429px)', { ssrWidth: 768 });

    const { hasConferenceEnded } = useConferenceStatus();

    const props = defineProps({
        headline: {
            type: String,
            default: '',
        },
        copy: {
            type: String,
            default: '',
        },
        year: {
            type: Number,
            default: 2026,
        },
        unscheduled: {
            type: Boolean,
            default: false,
        },
        treatAsHero: {
            type: Boolean,
            required: false,
            default: true,
        },
        contactId: {
            type: String,
            default: '',
        },
        tabs: {
            type: Boolean,
            default: true,
        },
        randomize: {
            type: Boolean,
            default: false,
        },
        // Public, read-only "shared schedule" mode (Schedule Sharing feature):
        // the feed is sourced from `shareToken` instead of the viewer's own
        // schedule, and all owner-only chrome (tab toggle, My Schedule action
        // bar, Find a Session, intro/hero copy) is hidden — the shared page
        // supplies its own hero and wraps this block read-only.
        shared: {
            type: Boolean,
            default: false,
        },
        shareToken: {
            type: String,
            default: '',
        },
        // Builder toggle: show the Schedule Builder promo banner above the tabs.
        // Auto-hidden once the viewer already has a builder-generated schedule and
        // only while the builder is live (showMySchedule). Default off.
        showBuilderBanner: {
            type: Boolean,
            default: false,
        },
    });

    const { $bus, $clientPosthog } = useNuxtApp();

    const route = useRoute();

    const tab = useRouteQuery('tab', 'sessions');
    // Re-sync the schedule from D1 whenever My Schedule becomes active, so the
    // calendar's "Find a Session" gaps match what the suggest endpoint validates
    // against (see syncScheduleFromD1 above). `immediate` covers a direct load
    // onto ?tab=myschedule; the watch covers switching into the tab and login.
    watch([tab, loggedIn], ([t, li]) => {
        if (t === 'myschedule' && li) syncScheduleFromD1();
    }, { immediate: true });
    // The view a tab opens in when the user hasn't chosen one: a saved schedule
    // reads best as a day grid, the full catalog as a list.
    const defaultViewFor = (t) => (t === 'myschedule' ? 'calendar' : 'list');

    // The view is DERIVED from the tab, never written into the URL as a default.
    //
    // It used to be seeded from the route and then corrected by a watcher on
    // `tab`, which wrote the incoming tab's default into the URL. That write was
    // indistinguishable from a view the visitor had actually asked for: a second
    // watcher saw `?view=` appear and pinned it, and the pin made every later tab
    // switch refuse to re-default. Landing on /agenda, clicking My Schedule, then
    // clicking back to All Sessions left the entire catalog rendering as ONE
    // day's calendar grid — no list at all (Shannon, UNB-710).
    //
    // The same write is why two routes into My Schedule produced different URLs:
    // arriving with the tab already set never fires the watcher, so the URL kept
    // no `view`, while clicking the tab added `view=calendar`. Same screen, two
    // URLs, and different behaviour afterwards — which is what made it look like
    // My Schedule appeared only sometimes.
    //
    // Deriving it removes both. `?view=` exists only when someone chose a view,
    // so its presence IS the pin — no separate flag to keep in step — and every
    // route into a tab produces the same URL.
    const viewParam = useRouteQuery('view', null);
    const view = computed({
        // Shared schedules open in Overview: a visitor comparing schedules wants
        // the whole day at a glance, not a single-day calendar or a flat list.
        get: () => viewParam.value || (props.shared ? 'overview' : defaultViewFor(tab.value)),
        set: (v) => { viewParam.value = v; },
    });
    // Choosing a view is just writing it: the param's presence pins it from then
    // on, including across tab switches.
    const setView = (v) => { view.value = v; };

    const time_range_val = useRouteQuery('time_range', [0, 100]);
    const page = useRouteQuery('page', 1);
    // Internal loading cursor. URL `page` is a UI-position marker updated from
    // scroll; loadingPage drives the actual fetch sequence. Seeded from URL so
    // SSR renders the requested page; client mount backfills earlier pages.
    const loadingPage = ref(parseInt(page.value, 10) || 1);
    const availability = useRouteQuery('availability', 'all');
    const day = useRouteQuery('day', 'all');
    const level = useRouteQuery('level', 'all');
    // Theme and Session Type each get a dedicated URL param so the backend
    // can filter each against a single session_* property instead of OR'ing
    // across every session_* property as the catch-all filter_tags does.
    const themes = useRouteQuery('themes', []);
    const sessionTypes = useRouteQuery('session_types', []);
    const seating = useRouteQuery('seating', 'all');
    const role = useRouteQuery('role', 'all');
    // Drawer-owned filter toggles (the param is null/absent when off so the URL
    // stays clean; '1' when on).
    const seatsNeededParam    = useRouteQuery('seats_needed',    null);
    const availableSeatsParam = useRouteQuery('available_seats', null);
    const onDemandParam       = useRouteQuery('on_demand',       null);
    const boolParam = (param) => computed({
        get: () => param.value === '1' || param.value === 'true',
        set: (v) => { param.value = v ? '1' : null; },
    });
    // "Show me only what's clashing" — the destination the conflict modal's
    // "Take Me to Fix This" sends people to (Jack, 2026-08-20). Unlike the two
    // seat filters this one is NOT applied server-side: a conflict can involve a
    // custom My Event, which the catalog query never sees, and the conflicting
    // set is already resolved by /api/v1/schedule/conflicts. So it narrows on
    // the client, after events have been merged in.
    const conflictsOnlyParam = useRouteQuery('conflicts', null);
    // Read from useScheduleConflicts rather than kept here: a second copy drifted
    // from the one the drawer and the My Event modal use, and a My Event edit
    // refreshed neither, so a cleared conflict kept reading as conflicted
    // (Jack, 2026-08-21). One cache, refreshed by everything that can change it.
    const scheduleConflicts = useScheduleConflicts();
    const conflictIds = scheduleConflicts.conflictIds;
    const conflictDays = computed(() => scheduleConflicts.conflictDays(props.year));
    const seatsNeeded    = boolParam(seatsNeededParam);
    const availableSeats = boolParam(availableSeatsParam);
    const onDemand       = boolParam(onDemandParam);
    const conflictsOnly  = boolParam(conflictsOnlyParam);

    const PER_PAGE = 15;

    const dayjs = useDayjs();
    const showTags = ref(false);
    const showSearch = ref(false);
    // My Schedule action bar → Schedule Preferences drawer (Phase 1).
    const showPrefsDrawer = ref(false);
    const grid = ref(null);
    const refreshTime = ref(null);

    // Partner Day is invitation-only (UNB-600): its tab exists only for a viewer
    // whose Bizzabo registration reads partner_day 'Approved'. The server is the
    // real gate — /api/v1/sessions returns nothing for that day to anyone else —
    // so this is presentation: without it a non-approved viewer gets a tab that
    // opens an empty day.
    //
    // Client-only, for the same reason as useScheduleControls: this page's SSR
    // HTML is CDN-cached and shared between viewers, so resolving the tab on the
    // server would freeze one viewer's access for everyone who got that cache
    // entry. Ships hidden, revealed after mount — which also keeps hydration
    // matching the cached markup.
    const partnerDayReady = ref(false);
    onMounted(() => { partnerDayReady.value = true; });
    const showPartnerDay = computed(() => partnerDayReady.value && canAccessPartnerDay(user.value));

    const dates = computed(() => {
        const options = view.value === 'calendar' ? date_options_cal_2026 : date_options_2026;
        if (showPartnerDay.value) return options;
        const partnerDay = getPartnerDay(props.year);
        return options.filter(d => d.value !== partnerDay);
    });


    // My Schedule is gated by the global release date (enableMyScheduleAfter,
    // via useScheduleControls) — the same time-release gate the Add buttons use —
    // rather than a per-block prop. showMySchedule is client-only (false during
    // SSR + until mount) by design, so the tab reveals per-browser once the
    // release passes, without a redeploy and without a hydration mismatch.
    const { showMySchedule } = useScheduleControls();

    // Schedule Builder promo banner (above the tabs). Renders only when the
    // editor enabled it AND the builder is live (showMySchedule — client-only,
    // so this is naturally SSR-hidden and cache-safe) AND the viewer hasn't
    // opened the builder three times yet. Never on the public shared-schedule
    // view. Logged-out visitors qualify and are routed to login when they click.
    const builderBannerVisible = computed(() =>
        props.showBuilderBanner
        && !props.shared
        && showMySchedule.value
        && scheduleBuilder.interactions.value < scheduleBuilder.interactionsLimit
    );

    // Whether the viewer has actually been through the Schedule Builder. The
    // builder is the only thing that writes scheduledGenerated, so a non-empty
    // list is the signal — sessions the user added by hand don't count.
    const hasBuiltSchedule = computed(() => (user.value?.scheduledGenerated?.length || 0) > 0);

    const onBuilderBannerBuild = () => {
        if (!loggedIn.value) { navigateTo('/login'); return; }
        openScheduleBuilder();
    };

    const tab_options = computed(() => {
        const opts = [{ label: 'All Sessions', value: 'sessions' }];
        if (showMySchedule.value) opts.push({ label: 'My Schedule', value: 'myschedule' });
        return opts;
    });

    // Guard against a bookmarked ?tab=myschedule URL when My Schedule isn't
    // released — coerce to All Sessions so the endpoint isn't queried for a
    // schedule. Deferred to onMounted: showMySchedule is transiently false
    // during SSR/pre-mount, and resetting then would wipe a valid ?tab=myschedule
    // even when the feature IS released.
    onMounted(() => {
        watch(showMySchedule, (released) => {
            if (!released && tab.value === 'myschedule') tab.value = 'sessions';
        }, { immediate: true });
    });

    const activeTabLabel = computed(() => {
        const found = tab_options.value.find(t => t.value === tab.value);
        return found ? found.label : tab_options.value[0]?.label || '';
    });

    const role_options = computed(() => {
        // Sessionboard's "Role" field syncs to D1 with type === 'Role'. The
        // list collapses to just "All Roles" until real data arrives.
        const roleTags = (data.value?.all_tags || []).filter(t => t.type === 'Role');
        return [
            { label: 'All Roles', value: 'all' },
            ...roleTags.map(t => ({ label: t.name, value: t.id })),
        ];
    });

    // Per-role descriptive copy, authored in Builder.io (`role-copy` data model)
    // so the client can edit without a deploy. Shown above the results when a
    // single role is selected. Fetched during SSR (where the Builder API key
    // resolves) and serialized into the payload, so switching roles client-side
    // just re-reads the already-loaded map — no client-side Builder fetch.
    const { data: roleCopyMap } = useAsyncData('agenda-role-copy', () => getRoleCopy(), { default: () => ({}) });

    const activeRoleCopy = computed(() => {
        if (!role.value || role.value === 'all') return null;
        const entry = roleCopyMap.value?.[role.value] || {};
        const heading = entry.heading || role_options.value.find(o => o.value === role.value)?.label || '';
        const copy = entry.copy || '';
        if (!heading && !copy) return null;
        return { heading, copy };
    });

    const controls = ref(null);

    const toSet = (v) => new Set(
        Array.isArray(v) ? v : (v ? String(v).split(',').filter(Boolean) : []),
    );
    const activeThemes       = computed(() => toSet(themes.value));
    const activeSessionTypes = computed(() => toSet(sessionTypes.value));
    // "More Filters" button reads as active (filled) when any theme/type filter
    // is applied — matches the count badge.
    const moreFiltersActive  = computed(() => (activeThemes.value.size + activeSessionTypes.value.size) > 0);

    const dropFrom = (model, id) => {
        const current = Array.isArray(model.value)
            ? [...model.value]
            : (model.value ? String(model.value).split(',') : []);
        model.value = current.filter(t => t !== id);
    };
    const removeTheme       = (id) => dropFrom(themes, id);
    const removeSessionType = (id) => dropFrom(sessionTypes, id);

    // "My Event" is a My-Schedule-only Session Type (UNB-582). All Sessions never
    // merges the viewer's custom events into the feed, and the shared schedule
    // has none at all, so the option would filter to nothing anywhere else.
    const showMyEventType = computed(() => tab.value === 'myschedule' && !props.shared);

    // Leaving My Schedule with "My Event" still checked would strand a filter the
    // viewer can neither see nor clear: the checkbox is gone from the drawer, but
    // the param survives in the URL, and on All Sessions it matches no catalog
    // session — so the page reads as empty for no visible reason. Drop it on the
    // way out, and on mount too, since a pasted ?tab=sessions&session_types=my-event
    // URL arrives already stranded rather than transitioning into it.
    //
    // Both guards matter: dropFrom assigns unconditionally, so without the `has`
    // check this rewrites the query on every single load, and running it during
    // SSR would rewrite the URL on the server (same reason the Calendar seeds its
    // default day in onMounted rather than inline).
    const dropStrandedMyEventType = () => {
        if (tab.value === 'myschedule') return;
        if (!activeSessionTypes.value.has(MY_EVENT_TYPE.id)) return;
        dropFrom(sessionTypes, MY_EVENT_TYPE.id);
    };
    watch(tab, dropStrandedMyEventType);
    onMounted(dropStrandedMyEventType);

    // First selectable conference day for the current view, honoring the
    // Calendar default day — skips the Partner Day so the calendar opens on
    // Wednesday (first full conference day) rather than Tuesday.
    const firstVisibleDay = computed(() => {
        const partnerDay = getPartnerDay(props.year);
        return dates.value.find(d => d.value !== 'all' && d.value !== partnerDay)?.value
            || dates.value.find(d => d.value !== 'all')?.value;
    });

    // A ?day=2026-09-15 link — shared, bookmarked, or simply the URL the reporter
    // filed this ticket from — would otherwise strand a non-approved viewer on a
    // day that has no tab, staring at an empty agenda (the server returns nothing
    // for it). Bounce them to the first day they can actually see.
    //
    // Declared here rather than beside `dates` above because it reads
    // firstVisibleDay, and `immediate: true` runs during setup — referencing the
    // computed before its const initializes would throw. Guarded on
    // partnerDayReady so it never runs during SSR, where showPartnerDay is false
    // for everyone and this would redirect the whole world off Partner Day.
    watch([showPartnerDay, day], ([canSee, currentDay]) => {
        if (!partnerDayReady.value || canSee) return;
        if (currentDay && currentDay === getPartnerDay(props.year)) {
            day.value = firstVisibleDay.value || 'all';
        }
    }, { immediate: true });

    // Calendar requires a single day, so entering it from "All Days" auto-selects
    // the first day. That auto-selection must NOT linger when the user toggles
    // back to a view that supports "All Days" — only a day the user picked
    // themselves should persist. Remember the day we auto-set; on leaving Calendar,
    // if the day is still exactly that (untouched), clear it back to "all".
    let calendarAutoDay = null;

    // The watcher below only fires when the view CHANGES, so a page that *opens* in
    // Calendar (a ?view=calendar link, or My Schedule's default) would otherwise sit
    // in Calendar with day="all" — a state Calendar can't render. Seed it on mount.
    // Client-only: doing this during SSR would rewrite the URL on the server.
    const seedCalendarDay = () => {
        if (view.value !== 'calendar' || day.value !== 'all' || !firstVisibleDay.value) return;
        day.value = firstVisibleDay.value;
        calendarAutoDay = day.value;
    };
    onMounted(seedCalendarDay);

    // ...and neither trigger covers arriving at Calendar from Calendar. Both
    // "View My Schedule" (useScheduleAction.js) and the header's My Schedule link
    // navigate to /agenda?tab=myschedule with NO day param, so a visitor already
    // on My Schedule drops `day` back to 'all'. That is not a remount, and the
    // view does not CHANGE either — it derives from the tab, which is unchanged —
    // so nothing re-seeds and Calendar is left with the one day value it cannot
    // draw (Jack, 2026-09-03).
    //
    // Same failure as Asana 1217708959827723 ("Take me to fix this"), which was
    // fixed only for the conflicts lens: that watcher early-returns unless
    // `conflicts=1`, and neither of these two entry points sets it. Watching `day`
    // itself is what generalises it — the seed condition is its own loop guard,
    // since once day is a real day the early return fires before assigning.
    watch(day, seedCalendarDay);

    watch(view, (newView, oldView) => {
        if (newView === 'calendar') {
            if (day.value === 'all' && firstVisibleDay.value) {
                day.value = firstVisibleDay.value;
                calendarAutoDay = day.value;
            }
        } else if (oldView === 'calendar') {
            if (calendarAutoDay !== null && day.value === calendarAutoDay) {
                day.value = 'all';
            }
            calendarAutoDay = null;
        }
        if (newView !== oldView) {
            $clientPosthog?.capture('agenda_view_changed', { view: newView, previous_view: oldView });
        }
    });

    // Partner Day redirect disabled — partner day sessions shown to all users
    // for the June launch. Re-enable for July when registration-gated.

    watch(tab, (newTab, oldTab) => {
        if (newTab !== oldTab) {
            $clientPosthog?.capture('agenda_tab_changed', { tab: newTab, previous_tab: oldTab, is_authenticated: loggedIn.value });
        }
        // Don't carry filters into the logged-out My Schedule tab — it's just a
        // login prompt, so a filter set the user can't see or change would still
        // be applied when they switched back. Deliberately not `immediate`:
        // `loggedIn` is briefly false while the session resolves, and clearing on
        // that would wipe a logged-in user's filters out from under them.
        if (newTab === 'myschedule' && !props.shared && !loggedIn.value) {
            clearAllFilters();
        }
    });

    watch(day, (newDay, oldDay) => {
        if (newDay !== oldDay) {
            $clientPosthog?.capture('agenda_filter_day_changed', { day: newDay, previous_day: oldDay });
        }
    });

    watch(level, (newLevel, oldLevel) => {
        if (newLevel !== oldLevel) {
            $clientPosthog?.capture('agenda_filter_level_changed', { skill_level: newLevel, previous_level: oldLevel });
        }
    });

    watch(seating, (newSeating, oldSeating) => {
        if (newSeating !== oldSeating) {
            $clientPosthog?.capture('agenda_filter_seating_changed', { seating: newSeating, previous_seating: oldSeating });
        }
    });

    watch(availability, (newAvail, oldAvail) => {
        if (newAvail !== oldAvail) {
            $clientPosthog?.capture('agenda_filter_availability_changed', { availability: newAvail, previous_availability: oldAvail });
        }
    });

    watch(role, (newRole, oldRole) => {
        if (newRole !== oldRole) {
            $clientPosthog?.capture('agenda_filter_role_changed', { role: newRole, previous_role: oldRole });
        }
    });

    const trackTimeRangeChange = useDebounceFn((newRange, oldRange) => {
        $clientPosthog?.capture('agenda_filter_time_range_changed', {
            time_range_start: newRange[0],
            time_range_end: newRange[1],
            previous_time_range_start: oldRange[0],
            previous_time_range_end: oldRange[1],
        });
    }, 500);

    watch(
        time_range_val,
        (newRange, oldRange) => {
            if (JSON.stringify(newRange) !== JSON.stringify(oldRange)) {
                trackTimeRangeChange(newRange, oldRange);
            }
        },
        { deep: true }
    );
    // Dev-only: reveal every card variant (see the note in `params`).
    const previewVariants = computed(() => import.meta.dev && route.query.variants !== undefined);


    const params = computed(() => {
        return {
            year: props.year || null,
            unscheduled: props.unscheduled || null,
            // Only Detail (list) view paginates — Overview & Calendar request the full set
            // Detail paginates at 15, but the conflict filter narrows on the
            // client — so a clash sitting on page 2 would simply not be there.
            // Ask for the whole schedule while it's on, as Calendar already does.
            page: view.value === 'list' && !conflictsOnly.value ? loadingPage.value : null,
            per_page: view.value === 'list' && !conflictsOnly.value ? PER_PAGE : null,
            themes: activeThemes.value.size ? [...activeThemes.value].join(',') : null,
            session_types: activeSessionTypes.value.size ? [...activeSessionTypes.value].join(',') : null,
            availability: availability.value === 'all' ? null : availability.value,
            day: view.value === 'overview' || day.value === 'all' ? null : day.value,
            skill_level: level.value === 'all' ? null : level.value,
            time_range_start: Array.isArray(time_range_val.value) ? time_range_val.value[0] : 0,
            time_range_end: Array.isArray(time_range_val.value) ? time_range_val.value[1] : 100,
            seating: seating.value === 'all' ? null : seating.value,
            role: role.value === 'all' ? null : role.value,
            order_by: 'day',
            view: view.value,
            // Shared-schedule mode (Schedule Sharing feature): the server
            // resolves the OWNER's schedule from the token, so the viewer's own
            // schedule must never be sent here.
            shareToken: props.shared ? props.shareToken : null,
            // My Schedule now reads from the user's saved schedule (My Schedule
            // feature), not session reservations (Save a Seat — feature F).
            schedule: (!props.shared && tab.value === 'myschedule') ? (user.value?.scheduled || []).join(',') : null,
            seats_needed: seatsNeeded.value ? 1 : null,
            available_seats: availableSeats.value ? 1 : null,
            on_demand: onDemand.value ? 1 : null,
            contact: props.contactId || null,
            ts: loggedIn.value && tab.value === 'myschedule' ? Date.now() : null,
            // `?variants` is a DEV-ONLY preview hatch, matching `?seats` in
            // useScheduleControls. The server already seeds one of every card
            // variant per conference day under `randomize` — full-session,
            // full-action-required, full-saved-seat and the rest — but that only
            // ever arrived as a Builder block prop, so there was no way to look
            // at those treatments in a browser without editing the CMS.
            //
            // import.meta.dev is stripped from the production build, so this
            // cannot ship. It matters here more than usual: `randomize` also
            // makes the API bypass the Partner Day and VIP Party gates, so it
            // must never be reachable from a URL in production.
            //
            // Pair it with `?seats` — two of the full treatments are gated on
            // seat reservations being open.
            randomize: (props.randomize || previewVariants.value) ? 1 : null,
        };
    });

    // Key the cached payload to the year (or shared token) so that navigating
    // between two pages that both render this block doesn't reuse the previous
    // page's results — useAsyncData deduplicates globally by key. Shared-view
    // mode must fold in the shareToken: /agenda and /schedule/shared/[token]
    // both render this block with year=2026, so without the shared/token
    // distinction they'd collide on the same key and briefly show the other
    // page's session set before the shareToken refetch lands.
    const dataKey = `sessions-agenda-${props.shared ? `shared-${props.shareToken}` : (props.year || 'all')}`;

    // Refetch only when the FETCH-RELEVANT params ACTUALLY change. The array-valued
    // route-query refs (time_range_val, themes, session_types) re-emit a NEW array
    // identity on ANY route change — including opening the session drawer (?drawer=)
    // — so watching them directly spuriously refetched the feed, flashing the
    // loader and losing the user's scroll position. Serializing the params (minus
    // the volatile `ts` cache-buster) means identity-only churn no longer triggers a
    // refetch — the same JSON-compare guard the pagination-reset watcher uses.
    const fetchSignature = computed(() => {
        const { ts, ...stable } = params.value;
        return JSON.stringify(stable);
    });
    const { data, pending, refresh } = useAsyncData(dataKey, () => $fetch('/api/v1/sessions', { params: { ...params.value } }), {
        // fetchSignature covers every filter param; loggedIn + refreshTime stay as
        // explicit auth-resolved / imperative refetch triggers.
        watch: [fetchSignature, loggedIn, refreshTime],
    });

    // `pending` is true for BACKGROUND refetches too (e.g. after removing one
    // session, which re-fetches to pick up a re-suggested replacement). Those must
    // NOT unmount the view or flash a loader — useAsyncData keeps the previous
    // `data` during a refresh, so we just render it until the new data lands.
    // User-initiated loads (first load, filter/day/view changes) still block.
    const backgroundRefresh = ref(false);
    const blockingLoad = computed(() => pending.value && !backgroundRefresh.value);

    // Infinite scroll — accumulate sessions in buckets keyed by page number.
    // Computed output sorts buckets by page so backfills from deep-linked URLs
    // (e.g. ?page=3 SSRs page 3 first, then client prepends pages 1-2) render
    // in correct source order.
    const pageBuckets = ref({});
    const loadingMore = ref(false);
    // Set before a schedule-driven refetch: the `schedule` query param changed, so
    // the server repaginates and merging the new page into the old buckets would
    // render a mix of the new and previous schedule states (stale/duplicate cards).
    // We swap them ATOMICALLY when the new data lands, so the old set stays on
    // screen during the fetch instead of momentarily collapsing to one page.
    const replaceBucketsOnNextData = ref(false);

    watch(() => data.value?.sessions, (newSessions) => {
        if (!newSessions) {
            loadingMore.value = false;
            return;
        }
        // Safety net: if we somehow ended up on a page beyond the result's range
        // (the response is an empty out-of-range page — current_page > pages),
        // snap back to page 1 so the list re-fetches and shows the real set
        // instead of wedging on an empty/partial accumulation.
        const respPage = parseInt(data.value?.current_page, 10) || 1;
        const respPages = parseInt(data.value?.pages, 10) || 1;
        if (respPage > respPages) {
            pageBuckets.value = {};
            replaceBucketsOnNextData.value = false;
            loadingMore.value = false;
            if (loadingPage.value !== 1) loadingPage.value = 1;
            return;
        }
        const cp = parseInt(data.value?.current_page || loadingPage.value, 10) || 1;
        const nextBucket = { [cp]: newSessions.map(s => ({ ...s, _page: cp })) };
        pageBuckets.value = replaceBucketsOnNextData.value
            ? nextBucket
            : { ...pageBuckets.value, ...nextBucket };
        replaceBucketsOnNextData.value = false;
        loadingMore.value = false;
    }, { immediate: true });

    const accumulatedSessions = computed(() => {
        const keys = Object.keys(pageBuckets.value).map(Number).sort((a, b) => a - b);
        const out = [];
        for (const k of keys) out.push(...pageBuckets.value[k]);
        return out;
    });

    // Fresh-session recommendation fix: page 1 is SSR-fetched, but that internal
    // $fetch carries no session cookie (auth is client-only), so a logged-in My
    // Schedule visitor gets an UNFILTERED, reason-less page 1 baked into the SSR
    // payload. If auth is already resolved at hydration, useAsyncData sees no
    // watched change and keeps that stale payload — only load-more pages re-fetch
    // with the cookie — so the first screen's cards render without their "why we
    // recommended this" reason. Once the viewer's contactId is known, discard that
    // payload and re-fetch from page 1 client-side (WITH the cookie + schedule
    // param → properly filtered AND reason-attached). Client-only on purpose:
    // never refetch during SSR, so the URL-keyed page cache stays anonymous and
    // can't bleed one user's per-user data to another (see the /api/v1/sessions
    // cache exclusion in nuxt.config).
    if (import.meta.client) {
        let reasonRefetchDone = false;
        watch(() => user.value?.contactId, async (contactId) => {
            // List view ONLY: page 1 is baked into the reason-less SSR payload and,
            // because the infinite-scroll observer keeps loadingPage advanced past 1,
            // the feed's own login-triggered refetch re-fetches that later page and
            // never repairs page 1. Overview/Calendar aren't paginated (page=null →
            // full set), so that same refetch already heals data.value for them — no
            // action needed here.
            if (reasonRefetchDone || !contactId || props.shared || tab.value !== 'myschedule' || view.value !== 'list') return;
            reasonRefetchDone = true;
            try {
                // Standalone client fetch (cookie present → server attaches reasons)
                // of page 1, written straight into bucket 1 so the reasoned rows MERGE
                // over the reason-less SSR page without disturbing pages the observer
                // has already accumulated. Client-only, so the URL-keyed SSR page
                // cache stays anonymous (no per-user cache bleed).
                const res = await $fetch('/api/v1/sessions', { params: { ...params.value, page: 1 } });
                if (res?.sessions) {
                    const cp = parseInt(res.current_page, 10) || 1;
                    pageBuckets.value = { ...pageBuckets.value, [cp]: res.sessions.map((s) => ({ ...s, _page: cp })) };
                }
            } catch (e) { /* reasons are a nice-to-have; never disrupt the view */ }
        }, { immediate: true });
    }

    // Per-user "added to My Schedule" overlay. The sessions feed no longer sets
    // user_added itself (it's per-user, not cacheable); this derives it client-side
    // from the user's own schedule list so it stays reactive to refreshSession()
    // and schedule:changed without needing a refetch.
    const scheduleIds = computed(() => user.value?.scheduled || []);

    // deriveUserAdded rewrites user_added on every row from the viewer's own
    // schedule, which would erase the seeded value under `?variants` — the two
    // action-required variants are defined by user_added, and the seeded sessions
    // are not on anyone's real schedule, so both silently disappeared.
    // In the preview only, keep whatever the server sent.
    // Both per-user overlays in one pass: whether the session is on the
    // schedule, and whether a seat / waitlist place is held. Neither can come
    // from the cached feed. Under `?variants` the seeded values stand instead.
    const applyUserAdded = (rows) => (
        previewVariants.value
            ? (rows || [])
            : deriveSeatState(deriveUserAdded(rows, scheduleIds.value), user.value)
    );

    // Merge in the user's custom events on the My Schedule tab only — the
    // catalog feed never contains them. Detail & Calendar are day-scoped (the
    // server filters the catalog to the selected day), so scope events to the
    // same day — otherwise a custom event appears under every selected day.
    // Overview fetches all days and groups client-side, so it keeps all events.
    // The custom events that survive the active filters.
    //
    // Split out from withEvents so the filter drawer's "See Results (N)" can count
    // them too. That count came straight from the server's `total`, which by
    // definition cannot see events merged on the client — filtering to My Event
    // with one custom event read "0 results" while See Results then showed it
    // (UNB-632).
    const visibleEvents = computed(() => {
        if (tab.value !== 'myschedule') return [];
        // Honour the Session Type filter (UNB-582). My Events are merged in here
        // rather than fetched, so the server's session_types filter can't see
        // them — without this they'd survive every type filter and sit in the
        // results next to the Deep Dives you actually asked for. With a filter
        // active they appear only when "My Event" is one of the picks; with no
        // filter at all, everything shows as before.
        if (activeSessionTypes.value.size && !activeSessionTypes.value.has(MY_EVENT_TYPE.id)) {
            return [];
        }
        // Honour the two seat filters. Same reasoning again: both are applied
        // server-side against the catalog, so neither can see a My Event merged
        // in here. Unlike Session Type and Time, though, there is no version of
        // these an event can satisfy — a My Event has no capacity, so it can
        // never be a seat you still need to save, and it can never have a seat
        // available. Either toggle therefore excludes them outright.
        if (seatsNeeded.value || availableSeats.value) {
            return [];
        }
        let events = normalizedEvents.value;
        if (view.value !== 'overview' && day.value && day.value !== 'all') {
            events = events.filter(e => e._event?.day === day.value);
        }
        // Honour the Time filter too (UNB). Same reasoning as the Session Type
        // filter above: My Events are merged here rather than fetched, so the
        // server's time_range filter never sees them — an 8:45pm event survived a
        // 9:00am-12:45pm range and sat in the results. overlapsTimeRange is the same
        // predicate sessions.get.js applies to the catalog, against the same
        // window the slider itself is scaled to.
        const tr = Array.isArray(time_range_val.value) ? time_range_val.value : [0, 100];
        if (Number(tr[0]) > 0 || Number(tr[1]) < 100) {
            // Overlap, matching the server's rule for sessions: an event running
            // through the selected window belongs in it even if it began before
            // the window opened (UNB).
            events = events.filter(e => overlapsTimeRange(e, {
                start: tr[0],
                end: tr[1],
                startHour: start_hour.value,
                endHour: start_hour.value + window_hours.value,
                tz: getConferenceTimezone(props.year),
            }));
        }
        return events;
    });

    // Per-user overlay, exactly like deriveUserAdded/deriveSeatState: the shared
    // cached feed cannot carry "this clashes with something else YOU saved".
    // Applied after withEvents so custom events get marked too, and skipped for
    // a shared schedule — those conflicts would be the OWNER's, not the viewer's.
    // Marks a clash wherever the session appears, NOT just on My Schedule
    // (Nick, Asana 1217708959827831). A conflict is a fact about the viewer's
    // schedule, so the full agenda showing the same session as an ordinary card
    // hid the very thing they need to act on. The My-Schedule gate that used to
    // sit here is kept on filterToConflicts below, where it belongs: showing the
    // state everywhere is right, but "conflicts only" is a My Schedule lens.
    const applyConflicts = (sessions) => {
        if (!conflictIds.value.size) return sessions;
        return (sessions || []).map((s) => (
            conflictIds.value.has(s.unique_id) ? { ...s, is_conflicted: true } : s
        ));
    };

    // Applied AFTER withEvents so conflicted My Events survive it — they are
    // part of a clash as often as catalog sessions are.
    const filterToConflicts = (sessions) => {
        if (!conflictsOnly.value || tab.value !== 'myschedule' || !sessions) return sessions;
        return sessions.filter((s) => conflictIds.value.has(s.unique_id));
    };

    const withEvents = (sessions) => (
        tab.value !== 'myschedule' ? sessions : [...sessions, ...visibleEvents.value]
    );

    // What the filter drawer reports. The server's total counts catalog sessions
    // only; the viewer's custom events are added on the client, so they have to be
    // added to the count as well or the drawer contradicts what it then shows.
    const filterResultsCount = computed(() => (
        conflictsOnly.value
            ? (gridData.value?.sessions?.length || 0)
            : (data.value?.total || 0) + visibleEvents.value.length
    ));

    const gridData = computed(() => {
        if (!data.value) return null;
        // Fall back to raw data when the per-page bucket accumulation hasn't
        // caught up yet (e.g. immediately after a view-switch refetch, before
        // the data.value?.sessions watcher has populated pageBuckets). Without
        // this, AgendaDetailList renders an empty grid even though the response
        // had sessions.
        if (!accumulatedSessions.value.length && data.value.sessions?.length) {
            return {
                ...data.value,
                sessions: props.shared
                    ? applyUserAdded(data.value.sessions)
                    : filterToConflicts(applyConflicts(withEvents(filterToScheduled(applyUserAdded(data.value.sessions))))),
            };
        }
        return {
            ...data.value,
            sessions: props.shared
                ? applyUserAdded(accumulatedSessions.value)
                : filterToConflicts(applyConflicts(withEvents(filterToScheduled(applyUserAdded(accumulatedSessions.value))))),
        };
    });

    // The return-visit prompt (Figma 4669:57041): the first time an attendee
    // opens My Schedule after seat reservations go live, list the
    // limited-capacity sessions they added earlier that still have no seat.
    //
    // Placed after gridData rather than beside the other tab watchers because
    // it reads gridData, view and seatsNeeded — all declared below that point,
    // and an `immediate` watcher runs during setup, where those are still in
    // the temporal dead zone.
    //
    // Keyed on My Schedule specifically: it is about that tab's contents, and
    // waiting for a populated grid avoids prompting off a half-hydrated
    // schedule. useSeatPrompt owns the once-only gate, so re-entering is safe;
    // seatPromptChecked just stops it re-running within a single visit.
    const seatPrompt = useSeatPrompt();
    const seatPromptChecked = ref(false);
    const conflictChecked = ref(false);

    // Populate the id set the `conflicts` filter narrows against. Re-run on any
    // schedule change too: removing one half of a clash has to drop the other
    // half out of the filtered list, or they sit there looking unresolved.
    const refreshConflictIds = async () => {
        // Loaded on EVERY tab, not just My Schedule (Nick, Asana
        // 1217708959827831). The card treatments read from this, and now that a
        // clash is marked wherever the session appears, skipping the load on the
        // full agenda left the set empty — so applyConflicts had nothing to mark
        // and the fix looked like it had not worked at all.
        if (!loggedIn.value) return;
        await scheduleConflicts.loadConflictIds();
    };
    watch([conflictsOnly, loggedIn, tab], refreshConflictIds, { immediate: true });
    $bus.on('schedule:changed', () => { refreshConflictIds(); });

    // Calendar renders exactly one day. Landing on a day with no clash would show
    // an empty grid — worse now that the grid closes down to the clashing span —
    // so move to the first day that has one.
    //
    // `day` is watched alongside the rest, which is what makes this cover the
    // case that opened Nick's ticket (Asana 1217708959827723). Every "Take Me to
    // Fix This" navigates to /agenda WITHOUT a day param, so arriving from a URL
    // that had one drops `day` back to 'all' — a combination Calendar cannot
    // render. When that navigation starts from Calendar it is not a view CHANGE,
    // so neither the view watcher nor onMounted re-seeds it, and without `day`
    // here nothing else fires either: conflictsOnly and view are unchanged, and
    // conflictDays only moves if that removal happened to clear a whole day.
    // Hence "sometimes", and hence worse while working through several conflicts,
    // where the day list usually survives each removal.
    //
    // The early return below is also the loop guard: once day is one of `days`,
    // re-entry exits before assigning.
    watch([conflictsOnly, view, conflictDays, day], ([on, v, days]) => {
        if (!on || v !== 'calendar' || !days.length) return;
        if (days.includes(day.value)) return;
        day.value = days[0];
    });
    watch(
        () => [tab.value, loggedIn.value, gridData.value?.sessions?.length],
        async ([t, li, count]) => {
            if (!li) return;

            if (t === 'myschedule' && count && !seatPromptChecked.value) {
                seatPromptChecked.value = true;
                await seatPrompt.maybeShowOnReturn(gridData.value?.sessions, () => {
                    // "Save Seats" lands on My Schedule's Detail view filtered to
                    // the sessions still needing one (annotation 4669:65271).
                    view.value = 'list';
                    seatsNeeded.value = true;
                });
            }

            // Any agenda visit, not just My Schedule: the rule is "logs in or
            // visits the agenda/schedule" (Jack, 2026-08-20), and conflicts are
            // resolved server-side, so this needs neither the tab nor the grid.
            //
            // It cannot stack with the seat prompt above despite not being
            // chained to it: maybeShow is gated on that prompt having been seen
            // and dismissed, and the prompt itself is once-ever — so in any one
            // visit at most one of the two can open.
            if (conflictChecked.value) return;
            conflictChecked.value = true;
            await scheduleConflicts.maybeShow(() => {
                // My Schedule, CALENDAR view, showing ONLY what clashes. Calendar
                // rather than the detail list because the clash is a shape — two
                // blocks occupying one slot — and the list can only assert it in
                // words (Nick, Asana 1217708959827705). Every other narrowing is cleared so
                // nothing can hide half of a pair.
                tab.value = 'myschedule';
                view.value = 'calendar';
                day.value = 'all';
                seatsNeeded.value = false;
                availableSeats.value = false;
                conflictsOnly.value = true;
            });
        },
        { immediate: true },
    );

    // Calendar uses the raw feed (not gridData); apply the same grace filter so a
    // just-removed event lingers there too. Shared mode mirrors the gridData
    // gating above: the server already narrowed the feed to the OWNER's
    // schedule via shareToken, so it must render bare (deriveUserAdded only) —
    // withEvents/filterToScheduled are viewer-schedule-relative (My Events +
    // the client-side "still on my schedule" grace filter) and would either
    // leak the viewer's own My Events onto the owner's calendar or drop owner
    // sessions that were never in the (irrelevant, possibly empty) viewer
    // schedule.
    const calendarData = computed(() => {
        if (!data.value) return data.value;
        return {
            ...data.value,
            // applyUserAdded on BOTH branches, as gridData does. The non-shared
            // branch used to skip it, so user_added / user_has_seat /
            // user_waitlisted were undefined on every calendar card and
            // `action-required` could never be true — no orange treatment, and no
            // saved-seat treatment either. The BUTTONS still behaved, because
            // useScheduleAction reads the session cookie rather than these
            // fields, which is what made it look like only the card styling was
            // wrong (Jack, 2026-08-20).
            sessions: props.shared
                ? applyUserAdded(data.value.sessions)
                : filterToConflicts(applyConflicts(withEvents(filterToScheduled(applyUserAdded(data.value.sessions))))),
        };
    });

    // Whether the schedule-style view (My Schedule tab, or the public shared
    // read-only equivalent) is the active rendering mode. Shared mode renders
    // the same schedule layout as My Schedule regardless of the `tab` query
    // param — the shared page may not carry `?tab=myschedule` at all.
    const isScheduleTab = computed(() => props.shared || tab.value === 'myschedule');

    // Logged-out My Schedule renders nothing but a login prompt, so there are no
    // sessions to filter — the chips and role copy are just noise there. A shared
    // schedule link is a different case: it shows real sessions, so it keeps them.
    const loggedOutSchedule = computed(() => !props.shared && tab.value === 'myschedule' && !loggedIn.value);

    // Whether to render the session lists vs the empty/logged-out state. On the
    // My Schedule tab the list shows ONLY when logged in AND there are scheduled
    // sessions — we must NOT key off the feed length there, because a logged-out
    // (or empty-schedule) request can still return rows (empty schedule param →
    // unfiltered feed), which would wrongly show the whole catalog instead of the
    // login prompt. Shared mode has no "viewer login" concept — the server has
    // already resolved the OWNER's feed via shareToken — so it uses the same
    // gridData-based branch as My Schedule.
    const hasFeed = computed(() => {
        // On My Schedule (or shared), "has feed" means there are SCHEDULED items
        // on the selected day. gridData is already filtered to the schedule (+
        // the day-scoped custom events), so use ONLY that. We must NOT fall back
        // to data.value.sessions here: an empty-schedule request returns the
        // UNFILTERED catalog for the day (14 rows on an empty day), which would
        // make hasFeed true, keep showList true (rendering the list's generic
        // "No sessions found"), and suppress the day-scoped-empty "Build My
        // Schedule For This Day" state. Only All Sessions may use the raw feed.
        if (isScheduleTab.value) return (gridData.value?.sessions?.length || 0) > 0;
        return ((gridData.value?.sessions?.length || data.value?.sessions?.length) || 0) > 0;
    });
    // Whether the user has ANYTHING on My Schedule (any day) — scheduled sessions
    // or a custom event. In shared mode there's no viewer-relative "any day"
    // signal available (scheduleIds/normalizedEvents are the VIEWER's own, not
    // the owner's) — fall back to hasFeed, the owner's shared-feed presence, so
    // a non-empty shared feed always satisfies this gate.
    const hasItems = computed(() => {
        if (props.shared) return hasFeed.value;
        return scheduleIds.value.length > 0 || normalizedEvents.value.length > 0;
    });

    const showList = computed(() => {
        if (isScheduleTab.value) {
            // Keep the list mounted while a removed card is still in its grace
            // window, so even removing the last scheduled item can animate out.
            // Shared mode bypasses the login requirement entirely — a public
            // shared schedule must render for a logged-out visitor.
            return (props.shared || loggedIn.value) && hasItems.value && hasFeed.value;
        }
        return hasFeed.value;
    });

    // The My Schedule action bar (Create My Event / Schedule Preferences / Add
    // to Calendar / Share) shows when the SELECTED DAY has scheduled content
    // (showList) OR the empty state is filter-driven (filteredEmpty, below) —
    // the user still has a real schedule to act on. It stays hidden only for a
    // genuinely empty day, where we show the "create your own events" intro +
    // the Build state instead. (Suppressed in shared mode at the call site.)
    const showActionBar = computed(() => tab.value === 'myschedule' && (showList.value || filteredEmpty.value));

    // Day-scoped empty: on a day-scoped view (Calendar always; Details with a
    // specific day) the user HAS a schedule but nothing on the selected day.
    // Distinct from the "nothing at all yet" state — gets its own "for this day"
    // copy and keeps the filter bar so the user can switch days.
    const dayScopedEmpty = computed(() =>
        isScheduleTab.value
        && (props.shared || loggedIn.value)
        && hasItems.value
        && !hasFeed.value
        && day.value !== 'all'
        && (view.value === 'calendar' || view.value === 'list')
    );

    // Overview isn't day-scoped (it always runs at day='all'), so dayScopedEmpty
    // can't cover it. Parallel gate: the user HAS a schedule but the active filters
    // zero out the Overview feed — gets the same reason-aware empty state as Detail.
    const overviewEmpty = computed(() =>
        isScheduleTab.value
        && (props.shared || loggedIn.value)
        && hasItems.value
        && !hasFeed.value
        && view.value === 'overview'
    );

    // Server-classified reason the My Schedule list is empty (Task: empty-state
    // reasons). Null for Calendar / non-empty / non-schedule.
    //
    // Reclassified client-side when a content filter is active. The server derives
    // this from getSchedule() — the catalog schedule alone — so it cannot see
    // custom events (merged in here, client-side) and does not know "My Event" is
    // a filter at all. Filter to My Event on a day whose catalog schedule is
    // empty and it reports no_events_on_day, i.e. "You haven't added anything to
    // My Schedule for this day" — a claim about the whole day, when all the user
    // actually did was filter. Whatever they filtered for is what's missing, so
    // this is a filter miss: "None of your scheduled sessions match these
    // filters", with View Full Schedule to undo it (UNB-582).
    //
    // Only the day-empty verdict is overridden — none_in_time_range and
    // no_filter_match are already filter-aware and correct.
    // "My Event" is the only active content filter — so the thing the viewer asked
    // for is their own events, and nothing else. Any other combination is a
    // regular filter miss.
    const myEventFilterOnly = computed(() =>
        activeSessionTypes.value.size === 1
        && activeSessionTypes.value.has(MY_EVENT_TYPE.id)
        && activeThemes.value.size === 0
    );

    // The viewer's events within the current scope, day-scoped exactly the way
    // withEvents scopes them (Overview shows every day, Detail/Calendar one).
    const eventsInScope = computed(() => {
        const events = normalizedEvents.value;
        if (view.value !== 'overview' && day.value && day.value !== 'all') {
            return events.filter(e => e._event?.day === day.value);
        }
        return events;
    });

    // ANY narrowing the viewer has applied — not just Session Type and Theme.
    // The day-empty verdict was reclassified on those two alone, so every other
    // filter (seat filters, on-demand, level, seating, availability, role, and
    // the `conflicts` narrowing) still produced "You haven't added anything to My
    // Schedule for this day" over a day the viewer had filled and then filtered
    // (Jack, 2026-08-20).
    //
    // The time range is deliberately absent: the server classifies that case as
    // none_in_time_range, which has its own, better copy.
    const filterNarrowed = computed(() =>
        activeSessionTypes.value.size > 0
        || activeThemes.value.size > 0
        || conflictsOnly.value
        || seatsNeeded.value
        || availableSeats.value
        || onDemand.value
        || level.value !== 'all'
        || seating.value !== 'all'
        || availability.value !== 'all'
        || role.value !== 'all'
    );

    const emptyReason = computed(() => {
        const reason = data.value?.empty_reason || null;

        // Filtered to My Event with none in scope. The server counts custom events
        // when it classifies (sessions.get.js folds getEvents into
        // scheduledDaySessions), but it has no idea `my-event` is a FILTER — so it
        // sees a day full of scheduled sessions and a filtered count of zero, and
        // reports no_filter_match: "None of your scheduled sessions match these
        // filters", offering View Full Schedule. The viewer wasn't looking for
        // sessions, and that action doesn't create the thing they're missing.
        // It also never classifies Calendar at all (that branch is list/overview
        // only) — and My Schedule DEFAULTS to Calendar, which is where this was
        // reported. So this verdict is decided here, client-side, where both the
        // filter and the events are known (UNB-582).
        if (myEventFilterOnly.value && eventsInScope.value.length === 0) return 'no_my_events';

        // Any other filter on a day the server calls empty: it's judging the whole
        // day when the viewer merely filtered. Reclassify as a miss.
        if (reason === 'no_events_on_day' && filterNarrowed.value) return 'no_filter_match';

        // The `conflicts` filter narrows on the CLIENT, so the server never sees
        // it: it reports a day full of sessions and no empty_reason at all, while
        // the view in front of the viewer is empty. Without this the null reason
        // falls through to the day-empty copy, which is exactly the untrue message
        // — they have added things for this day, they just can't see them.
        if (!reason && filterNarrowed.value) return 'no_filter_match';

        return reason;
    });

    // Case 2/3 are filter-driven empties: the user HAS a real schedule, it's just
    // narrowed out by the time range or content filters. Unlike a genuinely empty
    // day, the normal My Schedule action bar applies, and the lone
    // "Create My Event" intro (which is for empty schedules) is dropped.
    // 'no_my_events' belongs here too: the viewer has a real schedule and has
    // merely filtered to their own events, so the action bar still applies and
    // the "Create My Event" intro (which is for wholly empty schedules) would
    // otherwise duplicate the button this empty state already offers.
    const filteredEmpty = computed(() =>
        (dayScopedEmpty.value || overviewEmpty.value)
        && (emptyReason.value === 'none_in_time_range'
            || emptyReason.value === 'no_filter_match'
            || emptyReason.value === 'no_my_events')
    );

    // Hide the filter controls + results count when we're showing the My Schedule
    // empty/login state — there's nothing to filter there. The day-scoped-empty
    // state keeps them, so the user can pick another day.
    const showFilterBar = computed(() => !(isScheduleTab.value && !showList.value) || dayScopedEmpty.value || overviewEmpty.value);

    // Initialize cascading=true if URL has page>1, so the observer can't advance
    // loadingPage during SSR hydration while the backfill flow is getting ready.
    const cascading = ref((parseInt(page.value, 10) || 1) > 1);

    const handleLoadMore = () => {
        if (cascading.value || loadingMore.value || pending.value) return;
        if (!data.value) return;
        // Don't advance unless the CURRENT page came back full: a page with
        // fewer than PER_PAGE items is the last page, so there's nothing more to
        // load. This is the real infinite-scroll guard AND it stops the observer
        // from advancing off stale pre-filter data (e.g. a My Schedule day with
        // 9 sessions would otherwise request page 2 of a 1-page result — an
        // empty page — and wedge showing a partial set).
        if ((data.value.sessions?.length || 0) < PER_PAGE) return;
        const current = parseInt(loadingPage.value, 10) || 1;
        const total = parseInt(data.value.pages, 10) || 1;
        if (current < total) {
            // The agenda used to paginate, and `agenda_page_changed` fired from the
            // pager. Infinite scroll replaced the pager, but detail-list.vue only
            // emits `loadMore`, so the event silently died. Advancing a page IS the
            // infinite-scroll equivalent, so keep the event (and its shape) here —
            // it's still the "how deep did they go" signal.
            $clientPosthog?.capture('agenda_page_changed', {
                page: current + 1,
                previous_page: current,
                total_results: data.value?.total,
            });
            loadingMore.value = true;
            loadingPage.value = current + 1;
        }
    };

    // Every filter that narrows the SERVER query has to clear the accumulated
    // pages, because pageBuckets is keyed by page number and merged, not
    // replaced: a narrowed refetch overwrites bucket 1 and leaves buckets 2..n
    // holding results from the wider query. That is what "Only Show Seats I Need
    // to Save" looked like on the full agenda — the handful of matching orange
    // cards on top, then rows of unrelated sessions below them, left over from
    // pages the earlier scroll had already loaded (Nick, Asana 1217708959827838).
    //
    // The three drawer toggles were missing from this list. They only bite on the
    // full agenda: My Schedule sends no page/per_page while narrowed, so there is
    // never more than one bucket there to go stale.
    watch([time_range_val, day, level, tab, themes, sessionTypes, view, availability, role, seating, seatsNeeded, availableSeats, onDemand], (newVals, oldVals) => {
        // Only trigger if any value has actually changed
        if (JSON.stringify(newVals) !== JSON.stringify(oldVals)) {
            pageBuckets.value = {};
            loadingPage.value = 1;
            page.value = 1;
        }
    });

    const getTagNameById = (tagId) => {
        // "My Event" is synthetic (UNB-582): it's deliberately not a catalog tag,
        // so it's absent from all_tags and would resolve to '' — which rendered
        // the active-filter chip as a bare ✕ with no label.
        if (tagId === MY_EVENT_TYPE.id) return MY_EVENT_TYPE.name;
        if (!data?.value?.all_tags) return '';
        const tag = data.value.all_tags.find((tag) => tag.id === tagId);
        return tag ? tag.name : '';
    };

    // Resolve a role tag id to its label. Roles live in the same tag store as
    // topics; an "all" value is the absence-of-filter sentinel and shouldn't
    // render a chip.
    const getRoleName = (roleId) => {
        if (!roleId || roleId === 'all') return null;
        return getTagNameById(roleId);
    };

    const labelForOption = (options, value) => {
        if (!value || value === 'all') return null;
        const found = options.find((o) => o.value === value);
        return found ? found.label : value;
    };

    // One entry per active non-default filter — drives both the chip row above
    // results and the "Clear All" reset target.
    const activeFilterChips = computed(() => {
        const chips = [];
        for (const id of activeSessionTypes.value) {
            chips.push({ key: `session_type:${id}`, label: getTagNameById(id), onRemove: () => removeSessionType(id) });
        }
        for (const id of activeThemes.value) {
            chips.push({ key: `theme:${id}`, label: getTagNameById(id) || id, onRemove: () => removeTheme(id) });
        }
        // Role chip — mobile only. Desktop exposes the role select inline (like the
        // time range), so a chip there just repeats a control that's already visible.
        const roleName = getRoleName(role.value);
        if (roleName) chips.push({ key: 'role', label: roleName, mobileOnly: true, onRemove: () => { role.value = 'all'; } });

        const levelLabel = labelForOption(skill_level_options, level.value);
        if (levelLabel) chips.push({ key: 'level', label: levelLabel, onRemove: () => { level.value = 'all'; } });

        const seatingLabel = labelForOption(seating_options, seating.value);
        if (seatingLabel) chips.push({ key: 'seating', label: seatingLabel, onRemove: () => { seating.value = 'all'; } });

        const availabilityLabel = labelForOption(availability_options, availability.value);
        if (availabilityLabel) chips.push({ key: 'availability', label: availabilityLabel, onRemove: () => { availability.value = 'all'; } });

        if (conflictsOnly.value) chips.push({ key: 'conflicts', label: 'Conflicts Only', onRemove: () => { conflictsOnly.value = false; } });
        if (seatsNeeded.value) chips.push({ key: 'seats_needed', label: 'Seats I Need to Save', onRemove: () => { seatsNeeded.value = false; } });
        if (availableSeats.value) chips.push({ key: 'available_seats', label: 'Available Seats Only', onRemove: () => { availableSeats.value = false; } });
        if (onDemand.value) chips.push({ key: 'on_demand', label: 'On Demand Only', onRemove: () => { onDemand.value = false; } });

        // Day chip — mobile only, List view only. Calendar keeps its inline day
        // dropdown (can't clear to "all"); Overview always shows every day and
        // ignores the day filter, so a day chip there would be misleading.
        if (day.value && day.value !== 'all' && view.value === 'list') {
            const dayLabel = dates.value.find(d => d.value === day.value)?.label || day.value;
            chips.push({ key: 'day', label: dayLabel, mobileOnly: true, onRemove: () => { day.value = 'all'; } });
        }

        // Time range chip — mobile only, when not the default full range
        const tr = Array.isArray(time_range_val.value) ? time_range_val.value : [0, 100];
        if (Number(tr[0]) !== 0 || Number(tr[1]) !== 100) {
            chips.push({ key: 'time_range', label: `${startTime.value} – ${endTime.value}`, mobileOnly: true, onRemove: () => { time_range_val.value = [0, 100]; } });
        }

        return chips;
    });

    const clearAllFilters = () => {
        themes.value = [];
        sessionTypes.value = [];
        role.value = 'all';
        level.value = 'all';
        seating.value = 'all';
        availability.value = 'all';
        time_range_val.value = [0, 100];
        seatsNeeded.value = false;
        availableSeats.value = false;
        onDemand.value = false;
        conflictsOnly.value = false;
        // Clear the day too. Calendar must always have a concrete day, so reset it
        // to the first selectable day there rather than "all".
        day.value = view.value === 'calendar' ? (firstVisibleDay.value || 'all') : 'all';
        // A day we picked here is an auto-selection, not a user choice, so record it
        // the same way the view watcher does — otherwise leaving Calendar can't tell
        // it apart from a deliberate pick and the day lingers as a filter.
        calendarAutoDay = view.value === 'calendar' ? day.value : null;
        page.value = 1;
    };

    // "View All Sessions" — jump to the full All Sessions catalog across every day.
    // Calendar has no "all days" mode, so drop to the default all-days view first,
    // otherwise we'd land on the nonexistent calendar+all state.
    const viewAllSessions = () => {
        clearAllFilters();
        // No pin needed any more: there is no tab watcher to reset the view, and
        // writing `view` below puts it in the URL, which is what pins it.
        tab.value = 'sessions';
        // Read the raw param, not the derived view: `view` now depends on `tab`,
        // so reading it here would give a different answer depending on whether
        // the line above has settled. Only a PINNED calendar needs redirecting —
        // an underived one already resolves to All Sessions' all-days default.
        if (viewParam.value === 'calendar') view.value = 'overview';
        day.value = 'all';
    };

    // An empty day column on My Schedule → Overview is a dead end: you can't add
    // sessions from My Schedule, so there's nowhere to go from it (UNB-623). Hand
    // off to All Sessions in Details, scoped to that day. Same pin-then-switch
    // order as viewAllSessions, and `day` is set LAST because clearAllFilters()
    // resets it.
    const viewSessionsForDay = (dayValue) => {
        clearAllFilters();
        tab.value = 'sessions';
        view.value = 'list';
        day.value = dayValue;
    };

    // The agenda time-filter window (whole-hour start/end) for the selected day,
    // derived from the feed's day_bounds — the full possible set (all catalog
    // sessions + the viewer's custom events, before content filters). This makes
    // the slider (and the calendar grid, via startTime/endTime below) span every
    // session/event that day instead of a fixed 9am-8pm, so a late custom event
    // is reachable and empty tail hours don't show (UNB-609). Falls back to the
    // fixed window on All-Days / an empty day. The SAME helper runs server-side in
    // sessions.get.js, so the slider handles and the time_range filter agree.
    // day_window, NOT day_bounds. day_bounds is the Find-a-Session gap clamp and
    // must stay byte-identical to the suggest endpoint's own clamp (raw catalog);
    // widening it to include custom events broke that exact-instant match and made
    // Fill This Time Slot 400 (UNB-630). day_window is the separate agenda window
    // — viewer-visible catalog + their custom events — which is what the slider,
    // grid and time_range filter want.
    const agendaWindow = computed(() =>
        agendaWindowHours(data.value?.day_window || null, getConferenceTimezone(props.year))
    );
    const start_hour = computed(() => agendaWindow.value.startHour);
    const window_hours = computed(() => agendaWindow.value.endHour - agendaWindow.value.startHour);

    // Task 13 — `View Agenda` from the Find a Session "No Results" screen (spec
    // decision 19): jump to All Sessions, list view, the gap's own day,
    // time-filtered to the gap's own start/end. `gap` is null when reached from
    // the 'all' mode flow (findAllGaps() opens with gap: null) — there's no
    // single date/time to filter to in that case, so this is a no-op.
    const viewAgendaForGap = (gap) => {
        if (!gap?.date || !gap?.start || !gap?.end) return;

        clearAllFilters();
        tab.value = 'sessions';
        // clearAllFilters() just read `view.value` (still whatever it was, e.g.
        // 'calendar') to decide its own day fallback; view's watcher (above) only
        // ever fires asynchronously. Clear its own tracking var here, BEFORE
        // flipping `view`, so that deferred watcher can never find a stale
        // `calendarAutoDay` match and stomp the `day.value = gap.date` we're
        // about to set below back to 'all'.
        calendarAutoDay = null;
        view.value = 'list';
        day.value = gap.date;

        // Percentages across the day's dynamic window (start_hour..end_hour), in
        // conference-local (ET) time — never a fixed UTC offset, since ET
        // crosses a DST boundary and a hardcoded number would drift.
        const tz = getConferenceTimezone(props.year);
        const winStart = start_hour.value;
        const winHours = window_hours.value;
        const toPct = (iso) => {
            const local = dayjs(iso).tz(tz);
            const hours = local.hour() + local.minute() / 60;
            const pct = ((hours - winStart) / winHours) * 100;
            return Math.round(Math.max(0, Math.min(100, pct)));
        };
        time_range_val.value = [toPct(gap.start), toPct(gap.end)];
    };

    const startTime = computed(() => {
        const percent = Array.isArray(time_range_val.value) ? time_range_val.value[0] : 0;
        const totalMinutes = (percent / 100) * (window_hours.value * 60);
        const hoursToAdd = Math.floor(totalMinutes / 60);
        const minutesToAdd = Math.round((totalMinutes % 60) / 15) * 15;
        const date = dayjs()
            .tz(getConferenceTimezone(props.year))
            .hour(start_hour.value + hoursToAdd)
            .minute(minutesToAdd)
            .second(0)
            .millisecond(0);
        return date.format('h:mma');
    });

    const endTime = computed(() => {
        const percent = Array.isArray(time_range_val.value) ? time_range_val.value[1] : 100;
        const totalMinutes = (percent / 100) * (window_hours.value * 60);
        const hoursToAdd = Math.floor(totalMinutes / 60);
        const minutesToAdd = Math.round((totalMinutes % 60) / 15) * 15;
        const date = dayjs()
            .tz(getConferenceTimezone(props.year))
            .hour(start_hour.value + hoursToAdd)
            .minute(minutesToAdd)
            .second(0)
            .millisecond(0);
        return date.format('h:mma');
    });

    // Case 2 empty state: open Find a Session scoped to the SELECTED time-range
    // window on the current day, so it suggests sessions that fit that slot —
    // not every open gap. Mirrors startTime/endTime's pct→time math, anchored to
    // `day` (concrete here: Case 2 only renders in day-scoped Detail).
    // `windowed: true` tells the suggest endpoint to treat this as a time-range
    // window rather than requiring an exact open-gap match.
    const timeRangeBoundIso = (percent) => {
        const totalMinutes = (Math.max(0, Math.min(100, percent)) / 100) * (window_hours.value * 60);
        const h = start_hour.value + Math.floor(totalMinutes / 60);
        const m = Math.round((totalMinutes % 60) / 15) * 15;
        // 2-arg dayjs.tz (no format string) — the client dayjs doesn't load
        // customParseFormat, so the (input, format, tz) form isn't reliable here.
        return dayjs.tz(day.value, getConferenceTimezone(props.year))
            .hour(h).minute(m).second(0).millisecond(0)
            .toISOString();
    };
    const findSessionForTimeRange = () => {
        const tr = Array.isArray(time_range_val.value) ? time_range_val.value : [0, 100];
        openFindASession('gap', {
            date: day.value,
            start: timeRangeBoundIso(tr[0]),
            end: timeRangeBoundIso(tr[1]),
            windowed: true,
        });
    };

    // True only when the user has actually narrowed the time slider. At full
    // extent ([0,100]) there's no time restriction, so the calendar grid is free
    // to extend past 6pm to fit evening events (e.g. the VIP Party).
    const timeFiltered = computed(() => {
        const tr = Array.isArray(time_range_val.value) ? time_range_val.value : [0, 100];
        // Only the end handle gates the calendar grid's end cap. When the end is
        // at full extent the API skips the end filter and returns late sessions,
        // so the grid must extend to fit them even if the start handle was moved.
        return tr[1] < 100;
    });

    // Has the user chosen a time window at all? Either handle counts. The
    // calendar grid only closes down to its results when this is false —
    // otherwise a chosen window is shown in full, empty stretches included.
    const timeWindowed = computed(() => {
        const tr = Array.isArray(time_range_val.value) ? time_range_val.value : [0, 100];
        return tr[0] > 0 || tr[1] < 100;
    });

    // Counts custom events as well as sessions (UNB-672). data.total is the session
    // feed alone, which is right on All Sessions but undercounts My Schedule, where
    // the list is scheduled sessions PLUS the user's own events — filterResultsCount
    // is the same number the filter drawer's "See Results (N)" already reports.
    const results = computed(() => {
        if (!data.value) return '';
        return `${filterResultsCount.value} Results`;
    });

    const groupby = computed(() => {
        return 'location';
    });

    const handleLoginClick = async () => {
        $clientPosthog?.capture('agenda_login_prompt_clicked', {
            context: tab.value,
        });
        await navigateTo('/login');
    };

    const handleSearchClick = async () => {
        $clientPosthog?.capture('agenda_search_clicked');
        await navigateTo('/search?search=sessions');
    };

    const handleFilterDrawerToggle = () => {
        showTags.value = !showTags.value;
    };

    $bus.on('auth:login', async () => {
        await refreshSession();
        if (tab.value === 'myschedule') {
            setTimeout(() => refresh(dataKey), 250);
        }
    });

    $bus.on('sessions:registered', async () => {
        await refreshSession();
        if (tab.value === 'myschedule') {
            setTimeout(() => refresh(dataKey), 250);
        }
    });

    $bus.on('sessions:unregistered', async () => {
        await refreshSession();
        if (tab.value === 'myschedule') {
            setTimeout(() => refresh(dataKey), 250);
        }
    });

    // Refetch the My Schedule feed after the user's schedule changed. Callers must
    // ensure refreshSession() has already resolved, so params.schedule carries the
    // current ids. The accumulated pages are REPLACED (not merged) when the new
    // data lands, and the refresh is flagged as background so it never unmounts the
    // view or flashes a loader — the previous data stays on screen until then.
    const refetchSchedule = async () => {
        loadingPage.value = 1;
        page.value = 1;
        replaceBucketsOnNextData.value = true;
        backgroundRefresh.value = true;
        try {
            await refresh();
        } finally {
            backgroundRefresh.value = false;
        }
    };

    // Emitted by the Add/Remove to My Schedule card buttons with { session, removed },
    // AFTER the card's refreshSession() resolves. On removal, filterToScheduled()
    // already dropped the card — no refetch needed. On (re)add, the new session
    // isn't in the loaded feed, so refetch.
    //
    // That last clause is a CONDITION, and it used to be assumed rather than
    // checked: every non-removal refetched. Saving a seat also emits this event,
    // and on the seat path the session is already on the schedule and already on
    // screen — so the refetch fetched nothing new, but it did re-run the server's
    // `seats_needed` filter, which by then excluded the session precisely BECAUSE
    // the seat had just been saved. The card the attendee had clicked vanished
    // under the cursor, before the confirmation could be read.
    //
    // So test the feed rather than assuming: refetch only when the session really
    // isn't loaded. An already-visible card needs nothing from the server — the
    // per-user overlays (deriveUserAdded / deriveSeatState) repaint it in place
    // from the refreshed session, which is what makes the seat show up as saved.
    $bus.on('schedule:changed', (payload) => {
        const id = (payload?.session ?? payload)?.unique_id;
        if (tab.value !== 'myschedule' || !id) return;
        if (payload?.removed) return;
        const alreadyLoaded = accumulatedSessions.value.some((s) => s?.unique_id === id);
        if (!alreadyLoaded) refetchSchedule();
    });

    // A completed Schedule Builder run — from this block's buttons OR the nav's
    // /build-schedule deep link, which can fire on any page. The launcher has
    // already re-hydrated the session; the feed and custom events are ours to
    // reload.
    $bus.on('schedule:built', async () => {
        await myEvents.loadEvents();
        refresh(dataKey);
    });

    watch(
        () => loggedIn,
        async () => {
            if (loggedIn.value) {
                refreshTime.value = Date.now();
                setTimeout(() => refresh(dataKey), 250);
            }
        },
        { immediate: true }
    );

    // Track scroll to update URL page marker based on topmost visible card
    let scrollTicking = false;
    const onScrollForUrl = () => {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
            scrollTicking = false;
            const offset = 120;
            const cards = document.querySelectorAll('.sessions-grid li[data-page]');
            let currentPage = 1;
            for (const li of cards) {
                const rect = li.getBoundingClientRect();
                if (rect.top >= offset - 10) break;
                const p = parseInt(li.getAttribute('data-page') || '1', 10);
                if (p > currentPage) currentPage = p;
            }
            if (currentPage === 1 && page.value !== 1) {
                page.value = 1;
            } else if (currentPage > 1 && parseInt(page.value, 10) !== currentPage) {
                page.value = currentPage;
            }
        });
    };

    const waitForPage = (n) => new Promise((resolve) => {
        if (parseInt(data.value?.current_page, 10) === n) return resolve();
        const stop = watch(() => data.value?.current_page, (cp) => {
            if (parseInt(cp, 10) === n) {
                stop();
                resolve();
            }
        });
        setTimeout(() => { stop(); resolve(); }, 5000);
    });

    // Backfill earlier pages behind an SSR'd deep-linked page (e.g. ?page=3 SSRs
    // page 3, then on client mount we fetch pages 1 and 2 to prepend).
    const backfillFromPage = async (target) => {
        if (target <= 1) return;
        cascading.value = true;
        for (let p = 1; p < target; p++) {
            if (pageBuckets.value[p]) continue;
            try {
                const result = await $fetch('/api/v1/sessions', { params: { ...params.value, page: p } });
                if (result?.sessions) {
                    pageBuckets.value = {
                        ...pageBuckets.value,
                        [p]: result.sessions.map(s => ({ ...s, _page: p })),
                    };
                }
            } catch (e) {
                console.error('[agenda] backfill fetch failed for page', p, e);
            }
        }
        cascading.value = false;
        await nextTick();
        const firstCard = document.querySelector(`.sessions-grid li[data-page="${target}"]`);
        if (firstCard) {
            const y = firstCard.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: 'auto' });
        }
    };

    // Tell the 2026 header to use a flat pink sticky background (instead of its
    // translucent blur) only in the views that pin their own sticky headers
    // directly under the nav — the blur smears against them.
    //   • overview (either tab): sticky day-header row → flat pink
    //   • All Sessions calendar: sticky multi-column stage headers → flat pink
    //   • My Schedule calendar: a single-column personal schedule with NO stage
    //     headers (calendar-list falls back to singleColumn), so it keeps the
    //     blur (UNB-627 — Nick).
    //   • details/list: no sticky headers → blur.
    const stickyNavActive = useState('stickyNavActive', () => false);
    watch([view, tab], ([v, t]) => {
        stickyNavActive.value = v === 'overview' || (v === 'calendar' && t !== 'myschedule');
    }, { immediate: true });

    onMounted(() => {
        const targetPage = parseInt(page.value, 10) || 1;
        if (targetPage > 1) backfillFromPage(targetPage);
        window.addEventListener('scroll', onScrollForUrl, { passive: true });
    });

    onUnmounted(() => {
        stickyNavActive.value = false;
        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', onScrollForUrl);
        }
    });
</script>
<template>
    <div ref="rootRef" class="block-sessions-agenda relative overflow-clip bg-(--pink)" :class="{ 'is-hero': treatAsHero, 'is-shared': shared }">
        <!-- Hero shard belongs to whichever element is the page hero. When this
             block IS the hero (treatAsHero), it renders here, top-right under the
             nav. In shared mode the shared page renders its own hero + shard, so
             this one must not render (it would land in the filter area instead). -->
        <img v-if="treatAsHero" :src="'/img/2026/archive/hero-shard.png'"
            alt="" aria-hidden="true"
            class="hero-shard hidden lg:display-block absolute top-0 right-0 w-[678px] h-auto pointer-events-none z-0" />
        <div class="relative z-[1] px-(--box-padding-lr-m) md:px-(--box-padding-lr)" ref="container">
            <slot name="header">
                <ProjectBreadcrumbs v-if="treatAsHero" light />
                <!-- Shared mode: the shared page renders its own hero (owner-name
                     title + subtitle), so this block's title/description is
                     redundant and hidden. -->
                <div v-if="!shared" class="text-(--magenta) font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] mb-[30px] lg:mb-[60px]">
                    <h1 v-html="headline" class="font-hubspot-serif font-normal mb-[1.125rem] text-[36px] leading-[1.1] lg:text-[72px]"></h1>
                    <div v-html="copy" v-if="copy" class="max-w-[60rem] mb-0 copy"></div>
                </div>
            </slot>

            <!-- Schedule Builder promo banner — above the tabs, below the hero. -->
            <ScheduleBuilderBanner
                v-if="builderBannerVisible"
                class="mb-[35px] lg:mb-[48px]"
                @build="onBuilderBannerBuild" />

            <!-- Controls bar -->
            <div class="controls mb-[35px] lg:mb-[66px]" ref="controls" :class="{ 'is-cal': view === 'calendar', 'is-day': day !== 'all', 'has-tabs': tabs, 'is-myschedule': tab === 'myschedule' }">

                <!-- ============== Narrow-phone controls (3 stacked rows; < 430px) ============== -->
                <template v-if="isNarrow">
                    <!-- Row 1: Tab select. Mobile has no room for the desktop's side-by-side
                         tabs, so the same `tab` state is driven by a native select layered
                         over the label (styling the trigger, keeping the OS picker). -->
                    <label
                        v-if="tabs && !shared"
                        data-test="tab-toggle"
                        class="relative flex items-center gap-[10px] cursor-pointer w-fit mb-[23px] font-hubspot-serif font-normal text-[34px] leading-none text-(--magenta) whitespace-nowrap">
                        <span>{{ activeTabLabel }}</span>
                        <ChevronDown class="w-[13px] h-[12px] shrink-0" />
                        <select v-model="tab" aria-label="Select schedule" class="absolute inset-0 opacity-0 cursor-pointer appearance-none w-full h-full">
                            <option v-for="t in tab_options" :key="t.value" :value="t.value">{{ t.label }}</option>
                        </select>
                    </label>

                    <!-- Row 2: View pills (left) + Filters button (right) -->
                    <div class="flex items-center justify-between w-full mb-[23px]">
                        <div class="flex gap-px items-center shrink-0" role="group" aria-label="View mode">
                            <button
                                class="bg-white h-[44px] w-[50px] pl-[5px] flex items-center justify-center rounded-l-[50px] transition-colors duration-200"
                                :class="view === 'overview' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                @click="setView('overview')"
                                :aria-pressed="view === 'overview'"
                                aria-label="Overview view">
                                <AgendaViewOverview class="w-[13px] h-[14px]" />
                            </button>
                            <button
                                class="bg-white h-[44px] w-[47px] flex items-center justify-center transition-colors duration-200"
                                :class="view === 'list' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                @click="setView('list')"
                                :aria-pressed="view === 'list'"
                                aria-label="Details view">
                                <AgendaViewDetails class="size-[15px]" />
                            </button>
                            <button
                                class="bg-white h-[44px] w-[50px] pr-[2px] flex items-center justify-center rounded-r-[50px] transition-colors duration-200"
                                :class="view === 'calendar' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                @click="setView('calendar')"
                                :aria-pressed="view === 'calendar'"
                                aria-label="Calendar view">
                                <AgendaViewCalendar class="w-[14px] h-[15px]" />
                            </button>
                        </div>
                        <UButton
                            v-if="showFilterBar"
                            color="primary"
                            variant="outline"
                            icon="local-filter"
                            @click="handleFilterDrawerToggle"
                            class="more-filters"
                            :class="{ 'is-active': moreFiltersActive }"
                            :aria-expanded="showTags"
                            :aria-controls="showTags ? 'session-tags-drawer' : null">
                            Filters
                            <span v-if="(activeThemes.size + activeSessionTypes.size) > 0" class="more-filters-count">{{ activeTags.size }}</span>
                        </UButton>
                    </div>

                    <!-- Row 3: Search + Results count. Calendar view has no inline day
                         selector on mobile — the day lives in the Filters drawer.
                         Skip the whole row (and its margin) when it would be empty —
                         on the My Schedule empty states both children drop out, and a
                         bare margined row left a phantom gap above the empty message
                         (UNB-604). -->
                    <div v-if="showFilterBar || !isScheduleTab" class="flex items-center gap-[14px] mb-[1.3125rem]">
                        <ProjectSearchButton v-if="!isScheduleTab" class="search-button shrink-0" @click="handleSearchClick" />
                        <p
                            v-if="showFilterBar && data && data.total != null"
                            class="font-hubspot-sans text-[13px] leading-[1.5] tracking-[0.91px] uppercase text-[#7d4566] whitespace-nowrap">
                            {{ results }}
                        </p>
                    </div>
                </template>

                <!-- ============== Mobile controls (2 stacked rows; 430-1023px) ============== -->
                <template v-else-if="isMobile">
                    <!-- Row 1: Tab select (left) + View pills (right) -->
                    <div class="flex items-center justify-between w-full mb-[23px]">
                        <label
                            v-if="tabs && !shared"
                            data-test="tab-toggle"
                            class="relative flex items-center gap-[10px] cursor-pointer font-hubspot-serif font-normal text-[30px] leading-none text-(--magenta) whitespace-nowrap">
                            <span>{{ activeTabLabel }}</span>
                            <ChevronDown class="w-[13px] h-[12px] shrink-0" />
                            <select v-model="tab" aria-label="Select schedule" class="absolute inset-0 opacity-0 cursor-pointer appearance-none w-full h-full">
                                <option v-for="t in tab_options" :key="t.value" :value="t.value">{{ t.label }}</option>
                            </select>
                        </label>
                        <div class="flex gap-px items-center shrink-0" role="group" aria-label="View mode">
                            <button
                                class="bg-white h-[44px] w-[50px] pl-[5px] flex items-center justify-center rounded-l-[50px] transition-colors duration-200"
                                :class="view === 'overview' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                @click="setView('overview')"
                                :aria-pressed="view === 'overview'"
                                aria-label="Overview view">
                                <AgendaViewOverview class="w-[13px] h-[14px]" />
                            </button>
                            <button
                                class="bg-white h-[44px] w-[47px] flex items-center justify-center transition-colors duration-200"
                                :class="view === 'list' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                @click="setView('list')"
                                :aria-pressed="view === 'list'"
                                aria-label="Details view">
                                <AgendaViewDetails class="size-[15px]" />
                            </button>
                            <button
                                class="bg-white h-[44px] w-[50px] pr-[2px] flex items-center justify-center rounded-r-[50px] transition-colors duration-200"
                                :class="view === 'calendar' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                @click="setView('calendar')"
                                :aria-pressed="view === 'calendar'"
                                aria-label="Calendar view">
                                <AgendaViewCalendar class="w-[14px] h-[15px]" />
                            </button>
                        </div>
                    </div>

                    <!-- Row 2: Filters + Search (left) + Results (right). Calendar view has
                         no inline day selector on mobile — the day lives in the Filters drawer.
                         Skip the whole row (and its margin) when it would be empty — on the
                         My Schedule empty states every child drops out, and a bare margined
                         row left a phantom gap above the empty message (UNB-604). -->
                    <div v-if="showFilterBar || !isScheduleTab" class="flex items-center justify-between w-full mb-[1.3125rem]">
                        <div class="flex gap-[14px] items-center">
                            <UButton
                                v-if="showFilterBar"
                                color="primary"
                                variant="outline"
                                icon="local-filter"
                                @click="handleFilterDrawerToggle"
                                class="more-filters"
                            :class="{ 'is-active': moreFiltersActive }"
                                :aria-expanded="showTags"
                                :aria-controls="showTags ? 'session-tags-drawer' : null">
                                Filters
                                <span v-if="(activeThemes.size + activeSessionTypes.size) > 0" class="more-filters-count">{{ activeThemes.size + activeSessionTypes.size }}</span>
                            </UButton>
                            <ProjectSearchButton v-if="!isScheduleTab" class="search-button shrink-0" @click="handleSearchClick" />
                        </div>
                        <p
                            v-if="showFilterBar && data && data.total != null"
                            class="font-hubspot-sans text-[13px] leading-[1.5] tracking-[0.91px] uppercase text-[#7d4566] whitespace-nowrap text-right">
                            {{ results }}
                        </p>
                    </div>
                </template>

                <!-- ============== Desktop controls (2 rows) ============== -->
                <template v-else>
                    <!-- Top row: Tab toggle + View pills + Search (all left-aligned) -->
                    <div class="flex items-center gap-[40px] mb-[48px]">
                        <div v-if="tabs && !shared" data-test="tab-toggle" class="flex items-center gap-[40px]">
                            <button
                                v-for="t in tab_options"
                                :key="t.value"
                                class="font-hubspot-serif font-normal text-[42px] leading-none transition-colors duration-200"
                                :class="tab === t.value
                                    ? 'text-(--magenta) underline decoration-[#7d4566] decoration-dotted decoration-[4%] underline-offset-[7px]'
                                    : 'text-[#7d4566] hover:text-(--magenta) hover:underline hover:decoration-[#7d4566] hover:decoration-dotted hover:decoration-[4%] hover:underline-offset-[7px]'"
                                @click="tab = t.value">
                                {{ t.label }}
                            </button>
                        </div>

                        <!-- ml-auto only pays for itself when the tabs are there to sit
                             opposite. The shared schedule has no tabs (v-if above), so the
                             pills are this row's only child and ml-auto just strands them
                             on the right, away from the title, filters and cards that are
                             all left-aligned (UNB-590). -->
                        <div :class="['flex items-center gap-[20px]', shared ? '' : 'ml-auto']">
                            <div class="flex gap-px items-center" role="group" aria-label="View mode">
                                <button
                                    class="bg-white h-[44px] px-[20px] py-[10px] gap-[10px] flex items-center justify-center rounded-l-[50px] transition-colors duration-200"
                                    :class="view === 'overview' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                    @click="setView('overview')"
                                    :aria-pressed="view === 'overview'"
                                    aria-label="Overview view">
                                    <AgendaViewOverview class="size-[14px]" />
                                    <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase">Overview</span>
                                </button>
                                <button
                                    class="bg-white h-[44px] px-[20px] py-[10px] gap-[10px] flex items-center justify-center transition-colors duration-200"
                                    :class="view === 'list' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                    @click="setView('list')"
                                    :aria-pressed="view === 'list'"
                                    aria-label="Details view">
                                    <AgendaViewDetails class="size-[14px]" />
                                    <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase">Details</span>
                                </button>
                                <button
                                    class="bg-white h-[44px] px-[20px] py-[10px] gap-[10px] flex items-center justify-center rounded-r-[50px] transition-colors duration-200"
                                    :class="view === 'calendar' ? 'text-(--magenta)' : 'text-(--dark-pink) hover:text-(--magenta)'"
                                    @click="setView('calendar')"
                                    :aria-pressed="view === 'calendar'"
                                    aria-label="Calendar view">
                                    <AgendaViewCalendar class="size-[14px]" />
                                    <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase">Calendar</span>
                                </button>
                            </div>
                            <ProjectSearchButton v-if="!isScheduleTab" class="search-button" @click="handleSearchClick" />
                        </div>
                    </div>

                    <!-- Second row: Inline filters (left) + Results count (right) -->
                    <div v-if="showFilterBar" class="flex flex-wrap justify-between items-center gap-[1rem] mb-[29px]">
                        <div class="flex flex-wrap gap-[20px] items-center">
                            <USelect
                                v-if="view !== 'overview'"
                                v-model="day"
                                :items="dates"
                                class="day-select"
                                variant="outline"
                                :ui="{ content: 'ring-0 font-hubspot-serif text-white w-fit min-w-[var(--reka-select-trigger-width)] max-w-[80vw]', item: 'font-hubspot-serif text-white whitespace-nowrap' }" />

                            <div class="flex items-center gap-[14px] border border-(--magenta) rounded-[50px] px-[20px] py-[10px] h-[44px]">
                                <span class="font-hubspot-serif text-[17px] text-(--magenta) tracking-[0.34px] whitespace-nowrap w-[69px] shrink-0 text-left">{{ startTime }}</span>
                                <USlider
                                    v-model="time_range_val"
                                    :min-steps-between-thumbs="3"
                                    :step="1"
                                    variant="md"
                                    :ui="{ track: 'bg-(--dark-pink)', thumb: 'bg-(--pink) ring-(--magenta) time-slider-thumb' }"
                                    class="w-[231px]" />
                                <span class="font-hubspot-serif text-[17px] text-(--magenta) tracking-[0.34px] whitespace-nowrap w-[69px] shrink-0 text-right">{{ endTime }}</span>
                            </div>

                            <USelect
                                v-model="role"
                                :items="role_options"
                                class="role-select"
                                variant="outline"
                                :ui="{ content: 'ring-0 font-hubspot-serif text-white w-fit min-w-[var(--reka-select-trigger-width)] max-w-[80vw]', item: 'font-hubspot-serif text-white whitespace-nowrap' }" />

                            <UButton
                                color="primary"
                                variant="outline"
                                icon="local-filter"
                                @click="handleFilterDrawerToggle"
                                class="more-filters"
                            :class="{ 'is-active': moreFiltersActive }"
                                :aria-expanded="showTags"
                                :aria-controls="showTags ? 'session-tags-drawer' : null">
                                More Filters
                                <span v-if="(activeThemes.size + activeSessionTypes.size) > 0" class="more-filters-count">{{ activeThemes.size + activeSessionTypes.size }}</span>
                            </UButton>
                        </div>

                        <p
                            v-if="showFilterBar && data && data.total != null"
                            class="font-hubspot-sans text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-[#7d4566] whitespace-nowrap">
                            {{ results }}
                        </p>
                    </div>
                </template>

                <!-- Active filter chips: one per active filter type -->
                <!-- Guard on the OUTER wrapper too, not just the inner list — otherwise
                     the bare `mb-[1.3125rem]` div reserved a phantom gap above the empty
                     message whenever there were no chips (UNB-604). -->
                <div v-if="activeFilterChips.length && !loggedOutSchedule" class="mb-[1.3125rem]">
                    <div class="mb-[2.5rem] lg:mb-[2rem]" :class="{ 'lg:hidden': activeFilterChips.every(c => c.mobileOnly) }">
                        <ul class="flex flex-row flex-wrap gap-[1rem]">
                            <li v-for="chip in activeFilterChips" :key="chip.key" :class="{ 'lg:hidden': chip.mobileOnly }">
                                <ProjectTag
                                    :text="chip.label"
                                    ariaText="Remove Filter"
                                    colorSet="white"
                                    :useHover="true"
                                    :useX="true"
                                    @click="chip.onRemove"
                                    gtmEventName="agenda_filter_chip_removed" />
                            </li>
                            <li class="clear-all" :class="{ 'lg:hidden': activeFilterChips.every(c => c.mobileOnly) }">
                                <ProjectTag text="Clear All" colorSet="clear-all" :useHover="true" @click="clearAllFilters" />
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- My Schedule action bar (node 4583:184577 + the two link nodes)
                 — Create My Event / Schedule Preferences pills + Add to My
                 Calendar / Share My Schedule links. Shown once the user has a
                 schedule (across all three views); the empty/login states keep
                 their own CTAs. Calendar (Phase 2) and Share (Phase 3) are
                 wired to no-op handlers until their modals land. -->
            <!-- UNB-674, fourth pass. The bar's 40px plus the calendar's own 20px
                 padding-top put the carousel arrows 60px below these links. That
                 is right on desktop — Nick confirmed it, and there the links sit
                 inline with the pills — but below lg the links wrap onto their own
                 row, and 60px against this bar's own 16px row rhythm reads as a
                 void. Halved to 20px there, so the gap is 40px on a phone and
                 unchanged at 60px from lg up.
                 Scoped to Calendar: Detail and Overview open with content rather
                 than a mostly-empty grid header, so the same 60px does not read
                 as a gap there. -->
            <div
                v-if="showActionBar && !shared"
                data-test="ms-action-bar"
                class="flex flex-wrap items-center gap-x-[20px] gap-y-[16px]"
                :class="view === 'calendar' ? 'mb-[20px] lg:mb-[40px]' : 'mb-[40px]'"
            >
                <button
                    type="button"
                    data-test="ms-create-event"
                    class="inline-flex items-center gap-[10px] rounded-[50px] bg-(--magenta) text-white px-[20px] py-[10px] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 hover:bg-(--purple) whitespace-nowrap"
                    @click="openMyEvent()"
                >Create My Event</button>

                <!-- Preferences only exist once the builder has run — they're the inputs
                     it collected. Hidden for a hand-built schedule, which has none. -->
                <button
                    v-if="hasBuiltSchedule"
                    type="button"
                    data-test="ms-schedule-prefs"
                    class="inline-flex items-center gap-[8px] rounded-[50px] bg-[#7d4566] border border-[#7d4566] text-white px-[20px] py-[10px] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 hover:bg-(--magenta) hover:border-(--magenta) whitespace-nowrap"
                    @click="openSchedulePreferences"
                >
                    <svg viewBox="0 0 13 13" fill="none" aria-hidden="true" class="size-[13px] shrink-0">
                        <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                        <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
                    </svg>
                    Schedule Preferences
                </button>

                <!-- The 18px indent is the inline spacing only: on a wide viewport
                     these links sit on the same row as the pills, and the 18px on
                     top of the bar's own 20px column gap is what makes the 38px
                     separation from Schedule Preferences. Below lg the group wraps
                     onto its own line, where that same padding became an 18px
                     indent against the buttons above it and the page container
                     (UNB). Gated at lg because that's where the row measures out:
                     at 1024px the links are still inline (38px gap), by 820px they
                     have already wrapped. -->
                <div class="flex flex-wrap items-center gap-x-[38px] gap-y-[16px] lg:pl-[18px]">
                    <button
                        type="button"
                        data-test="ms-add-calendar"
                        class="font-hubspot-sans font-medium text-[13px] leading-[1.5] tracking-[0.91px] uppercase text-[#7d4566] underline decoration-dotted underline-offset-[3px] cursor-pointer bg-transparent border-none p-0 hover:text-(--magenta) hover:no-underline whitespace-nowrap"
                        @click="openAddToCalendar"
                    >Add to My Calendar</button>
                    <button
                        type="button"
                        data-test="ms-share"
                        class="font-hubspot-sans font-medium text-[13px] leading-[1.5] tracking-[0.91px] uppercase text-[#7d4566] underline decoration-dotted underline-offset-[3px] cursor-pointer bg-transparent border-none p-0 hover:text-(--magenta) hover:no-underline whitespace-nowrap"
                        @click="openShareSchedule"
                    >Share My Schedule</button>
                </div>
            </div>

            <!-- Role copy: heading + description shown when a single role is selected.
                 Sits BELOW the action bar (Asana UNB-606). The two were the other way
                 round, which pushed the action bar — the thing you act on — below a
                 block of descriptive copy that can run several lines. Order down the
                 page is now: filter bar → applied filter pills → action bar → role. -->
            <div v-if="activeRoleCopy && !loggedOutSchedule" data-test="ms-role-copy" class="flex flex-col gap-[20px] items-start max-w-[900px] text-(--magenta) mb-[40px] lg:mb-[48px]">
                <h2 v-if="activeRoleCopy.heading" class="font-hubspot-serif font-normal text-[36px] leading-[1.2] m-0">{{ activeRoleCopy.heading }}</h2>
                <div v-if="activeRoleCopy.copy" class="role-copy-body font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px]" v-html="activeRoleCopy.copy"></div>
            </div>

            <!-- View dispatch -->
            <template v-if="showList">
                <AgendaOverviewList
                    v-if="view === 'overview'"
                    :data="gridData || data"
                    :loading="blockingLoad"
                    :year="year"
                    :day="day"
                    :randomize="randomize"
                    @viewDay="viewSessionsForDay" />
                <AgendaDetailList
                    v-if="view === 'list'"
                    :data="gridData || data"
                    :loading="loadingMore"
                    :infinite="true"
                    :year="year"
                    @loadMore="handleLoadMore" />
                <AgendaCalendarList
                    v-if="view === 'calendar' && !blockingLoad"
                    :day="day"
                    :data="calendarData || data"
                    :year="year"                    :groupby="groupby"
                    :conflicts-only="conflictsOnly"
                    :start_time="startTime"
                    :end_time="endTime"
                    :time-filtered="timeFiltered"
                    :time-windowed="timeWindowed"
                    :my-schedule-tab="tab === 'myschedule' && !shared"
                    @findGap="openFindASession('gap', $event)"
                    @findAll="openScheduleBuilder" />
                <div class="flex flex-col items-center justify-center lg:min-h-[20rem]" v-if="blockingLoad && !loadingMore">
                    <FragmentsLoading loading />
                </div>
            </template>
            <template v-else>
                <!-- Loading: arriving from another page, the feed hasn't landed
                     yet and "no sessions match your selections" flashed before the
                     catalog appeared (UNB). Same guard the My Schedule empty below
                     already has (687688) — an empty result is only meaningful once
                     the request has finished. -->
                <div v-if="tab === 'sessions' && blockingLoad"
                    class="empty flex flex-col items-center justify-center py-[90px]">
                    <FragmentsLoading loading />
                </div>

                <!-- Empty state: All Sessions tab -->
                <div class="empty flex flex-col items-center justify-center py-[90px]" v-else-if="tab === 'sessions'">
                    <h2 class="mb-[2rem]">There are no sessions that match your selections</h2>
                    <p class="mb-[2rem]">Please try expanding your filtering criteria or clear your selections and start again.</p>
                    <div class="actions">
                        <button
                            class="reset-filters rounded-[50px] bg-transparent border border-(--magenta) text-(--magenta) px-5 py-2.5 font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 hover:bg-(--magenta) hover:text-white"
                            @click="clearAllFilters">
                            Reset All Filters
                        </button>
                    </div>
                </div>

                <!-- Empty state: My Schedule tab -->
                <div v-if="tab === 'myschedule'">
                    <!-- Logged out. On mobile this sits directly under the controls
                         row (29px gap) and above the agenda block's 24px bottom
                         padding, which stacked with py-[60px] into ~89px above /
                         ~84px below the message (UNB-604). Pull it flush under the
                         controls (-mt) and offset the wrapper's bottom padding (-mb)
                         so the message lands at a clean 60px top/bottom. Reset at lg,
                         where the roomier spacing is intended. -->
                    <div v-if="!loggedIn" class="empty flex flex-col items-center justify-center py-[60px] lg:py-[90px] -mt-[29px] -mb-[24px] lg:mt-0 lg:mb-0">
                        <h2>You must be logged in to view <span>My Schedule</span></h2>
                        <p>
                            In order to add sessions to My Schedule, you must have <nuxt-link to="/register">purchased registration</nuxt-link> for UNBOUND and you must be logged in. Once logged in, simply click Add to My Schedule on any session to add it here. You'll also be able to create your own custom events such as lunch, meetings, or time to check out any on-site experiences.
                        </p>
                        <div class="actions">
                            <button type="button" class="ms-cta-outline" @click="handleLoginClick">Login</button>
                            <button type="button" class="ms-cta-outline" @click="viewAllSessions">View All Sessions</button>
                        </div>
                    </div>

                    <!-- Logged in with nothing added -->
                    <template v-else>
                        <!-- Loading: the schedule feed hasn't landed yet (e.g. mid tab
                             toggle). Show the loader instead of flashing "you haven't
                             added anything" before the data arrives (687688). -->
                        <div v-if="blockingLoad" class="empty flex flex-col items-center justify-center py-[60px] lg:py-[90px]">
                            <FragmentsLoading loading />
                        </div>

                        <template v-else>
                        <!-- Intro: create your own events. Shown only for a
                             genuinely empty schedule — a filter-driven empty
                             (Case 2/3) keeps the full action bar instead, so the
                             lone Create-My-Event intro is dropped there. The
                             negative margin-top pulls it under the (hidden) filter
                             bar in the generic empty; in the day-scoped empty the
                             filters ARE shown, so drop the pull-up (has-filters). -->
                        <div v-if="!filteredEmpty" class="myschedule-intro" :class="{ 'has-filters': dayScopedEmpty || overviewEmpty }">
                            <p>Create and add your own events to My Schedule such as meetings, lunch, or time on the show floor to check out the experiences, activations and sponsors.</p>
                            <button type="button" class="ms-cta-filled" @click="openMyEvent()">Create My Event</button>
                        </div>

                        <!-- Day-scoped empty: a schedule exists, but the selected
                             day (Calendar / Details) has nothing to show. Every
                             view now renders the reason-aware fragment, so the
                             message names the ACTUAL cause — empty day, empty time
                             range, or filters — rather than one blanket line.
                             Calendar used to opt out and hardcode the day-empty
                             copy for all three, which is what made it claim
                             nothing was on a day the viewer had filled and then
                             filtered. Its old markup was a near-duplicate of the
                             fragment's day-empty case (same heading and actions),
                             so it's gone rather than kept in sync by hand. -->
                        <ScheduleEmptyState
                            v-if="overviewEmpty || dayScopedEmpty"
                            :reason="emptyReason"
                            :view="view"
                            @build="openScheduleBuilder"
                            @findSession="findSessionForTimeRange"
                            @clearFilters="clearAllFilters"
                            @viewAll="viewAllSessions"
                            @createEvent="openMyEvent()" />

                        <!-- Nothing added at all yet. -->
                        <div v-else class="empty flex flex-col items-center justify-center py-[60px] lg:py-[90px]">
                            <h2>You haven't added anything to My Schedule yet</h2>
                            <p>
                                Simply click Add to My Schedule on any session to add it here, or click Create My Event to add your own event such as lunch, meetings, or time to check out any on-site experiences. You can also use our schedule builder to create a schedule just for you.
                            </p>
                            <div class="actions">
                                <button type="button" class="ms-cta-filled ms-cta-build" @click="openScheduleBuilder">
                                    <svg viewBox="0 0 13 13" fill="none" class="size-[13px] shrink-0" aria-hidden="true">
                                        <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                                        <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
                                    </svg>
                                    Build My Schedule
                                </button>
                                <button type="button" class="ms-cta-outline" @click="viewAllSessions">View All Sessions</button>
                            </div>
                        </div>
                        </template>
                    </template>
                </div>
            </template>

            <!-- Only when there's a list to scroll. On the empty / login states it has
                 nothing to scroll back to, and its 36px + 2×38px margins were adding
                 112px of dead space under the empty copy. -->
            <GlobalBackToTop v-if="showList" />
        </div>

        <FragmentsDrawersAgendaFilters
            v-model:open="showTags"
            v-model:themes="themes"
            v-model:sessionTypes="sessionTypes"
            v-model:day="day"
            v-model:time_range="time_range_val"
            v-model:skill_level="level"
            v-model:seating="seating"
            v-model:availability="availability"
            v-model:role="role"
            v-model:seatsNeeded="seatsNeeded"
            v-model:availableSeats="availableSeats"
            v-model:onDemand="onDemand"
            :objects="filterResultsCount"
            :year="year"
            :day-options="dates"
            :role-options="role_options"
            :start-time="startTime"
            :end-time="endTime"
            :randomize="randomize"
            :show-my-event-type="showMyEventType"
            :show-advanced-filters="isMobile" />

        <FragmentsDialogsSearch v-model:open="showSearch" :options="{ requestOptions: { facetFilters: ['type:Session'] } }"></FragmentsDialogsSearch>

        <!-- My Schedule action bar → Schedule Preferences edit drawer (Phase 1). -->
        <FragmentsDrawersSchedulePreferences v-model:open="showPrefsDrawer" />
    </div>
</template>

<style>
    /* Enlarge the time-slider thumb touch target on touch devices (coarse
       pointers) without changing its visual size: a transparent ~44px hit area
       centered on the thumb via a pseudo-element. The thumb is positioned by
       Reka (containing block), so the inset anchors to it. */
    @media (pointer: coarse) {
        .time-slider-thumb::after {
            content: '';
            position: absolute;
            inset: -14px;
            border-radius: 9999px;
        }
    }

    /* Block root — background images and padding */
    .block-sessions-agenda {
        --mobile-width: calc(90vw);
        position: relative;
        padding: 43px 0 28px;
    }

    /* Back-to-top positioning. */
    .block-sessions-agenda .back-to-top {
        min-height: 36px;
        margin-top: 2.375rem;
        margin-bottom: 2.375rem;
    }

    .block-sessions-agenda .back-to-top.is-fixed > button,
    .block-sessions-agenda .back-to-top.offscreen > button {
        left: var(--box-padding-lr-m);
    }

    @media (min-width: 1024px) {
        .block-sessions-agenda .back-to-top.is-fixed > button,
        .block-sessions-agenda .back-to-top.offscreen > button {
            left: var(--box-padding-lr);
        }
    }

    .block-sessions-agenda .back-to-top:not(.is-fixed):not(.offscreen) > button {
        left: 0;
        bottom: 0;
        transition-property: opacity, transform, background-color, color;
    }

    @media (min-width: 1024px) {
        .block-sessions-agenda {
            padding: 75px 0 28px;
            padding-top: 6.0625rem;
        }
    }

    /* Hero variant */
    .block-sessions-agenda.is-hero {
        margin-top: calc(var(--header-height) * -1);
        padding-top: 97px;
        padding-bottom: var(--box-padding-tb-m);
    }

    @media (min-width: 1024px) {
        .block-sessions-agenda.is-hero {
            padding-top: 150px;
        }
    }

    /* Shared mode: the shared page renders its own hero above this block, so the
       normal top padding (which clears the nav when the block IS the hero) is just
       empty pink here — collapse it so the controls sit right under the hero.
       Higher specificity than the base/desktop rules above, so it wins everywhere. */
    .block-sessions-agenda.is-shared {
        padding-top: 0;
    }

    /* Copy paragraph spacing */
    .block-sessions-agenda .copy p {
        margin-bottom: 0.5rem;
    }

    /* Controls — calendar view state */
    .block-sessions-agenda .controls.is-cal {
        margin-bottom: 1.3125rem;
    }

    @media (min-width: 1024px) {
        .block-sessions-agenda .controls.is-cal {
            margin-bottom: 2rem;
        }
    }

    /* On My Schedule, tighten the gap below the controls to 29px (design) — the
       action bar / content sits 29px under the filters. Keyed on the tab (an
       SSR-stable URL query) rather than the client-only login state, so server
       and client agree and Chrome doesn't get stuck on the pre-hydration class.
       Overrides both the base lg:mb-[66px] utility and the is-cal state above;
       the is-cal.is-myschedule selector out-specifies the is-cal rule. */
    .block-sessions-agenda .controls.is-myschedule,
    .block-sessions-agenda .controls.is-cal.is-myschedule {
        margin-bottom: 29px;
    }

    /* Active tags — button sizing */
    .block-sessions-agenda .active-tags ul li button {
        font-size: calc(11 / var(--base-fs) * 1rem);
        padding: 0.3rem 0.6rem;
        letter-spacing: 0.034375rem;
    }

    /* Empty state — text styling */
    /* h4 type scale — 30px / 130%. */
    .block-sessions-agenda .empty h2 {
        text-align: center;
        font-family: var(--font-hubspot-serif);
        font-size: 1.875rem;
        font-weight: 400;
        letter-spacing: 0;
        margin-bottom: 1.875rem;
        max-width: 56.25rem;
        line-height: 1.3;
        color: var(--magenta);
    }

    /* Standard body copy — 16px / 26px. */
    .block-sessions-agenda .empty p {
        text-align: center;
        margin-bottom: 1.875rem;
        max-width: 52rem;
        font-family: var(--font-hubspot-sans);
        font-weight: 300;
        font-size: 1rem;
        line-height: 1.625rem;
        letter-spacing: 0.32px;
        color: var(--magenta);
    }

    .block-sessions-agenda .empty p a {
        color: var(--magenta);
        text-decoration: underline;
        text-decoration-style: dotted;
        text-decoration-color: #7d4566;
        text-underline-offset: 0.25rem;
    }

    /* Standard body-copy link hover: drop the underline, go purple. */
    @media (hover: hover) {
        .block-sessions-agenda .empty p a:hover {
            color: var(--purple);
            text-decoration: none;
        }
    }

    .block-sessions-agenda .more-filters,
    .block-sessions-agenda .day-select,
    .block-sessions-agenda .role-select {
        color: var(--magenta);
        border-color: var(--magenta);
        border-width: 1px;
        border-radius: 50px;
        box-shadow: none;
        font-family: var(--font-hubspot-serif);
        font-size: calc(17 / var(--base-fs) * 1rem);
        line-height: 1.4;
        letter-spacing: 0.34px;
        padding: 10px 20px;
    }

    .block-sessions-agenda .day-select,
    .block-sessions-agenda .role-select {
        padding-right: 42px;
    }

    /* Fixed widths so selecting a (long) option doesn't reflow the controls row.
       The trigger value truncates with an ellipsis; the full label still shows
       in the dropdown and as a chip once selected. */
    .block-sessions-agenda .day-select {
        width: 180px;
    }

    .block-sessions-agenda .role-select {
        width: 230px;
    }

    .block-sessions-agenda .day-select span,
    .block-sessions-agenda .role-select span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
    }

    /* The chevron glyph renders ~2px above the field's vertical center, so it sat
       higher than the (centered) label text. Nudge it down to line up with the
       label. */
    .block-sessions-agenda .day-select .iconify,
    .block-sessions-agenda .role-select .iconify {
        transform: translateY(2px);
    }

    .block-sessions-agenda .more-filters {
        gap: 10px;
    }

    .block-sessions-agenda .more-filters .more-filters-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: var(--magenta);
        color: #fff;
        font-family: var(--font-hubspot-serif);
        font-size: 11px;
        line-height: 1;
    }

    .block-sessions-agenda .more-filters:hover .more-filters-count {
        background: #fff;
        color: var(--magenta);
    }

    .block-sessions-agenda .more-filters:hover,
    .block-sessions-agenda .day-select:hover,
    .block-sessions-agenda .role-select:hover {
        background: var(--magenta);
        color: white;
        border-color: var(--magenta);
    }

    /* Active state: filters applied → filled magenta with an inverted count
       badge. Hover deepens to purple (per Figma). */
    .block-sessions-agenda .more-filters.is-active {
        background: var(--magenta);
        color: white;
        border-color: var(--magenta);
    }

    .block-sessions-agenda .more-filters.is-active .more-filters-count {
        background: #fff;
        color: var(--magenta);
    }

    .block-sessions-agenda .more-filters.is-active:hover {
        background: var(--purple);
        color: white;
        border-color: var(--purple);
    }

    .block-sessions-agenda .more-filters.is-active:hover .more-filters-count {
        background: #fff;
        color: var(--purple);
    }

    .block-sessions-agenda .empty h2 span {
        color: var(--magenta);
    }

    .block-sessions-agenda .empty .actions {
        display: flex;
        gap: 1.5625rem;
    }

    .block-sessions-agenda .empty button {
        width: fit-content;
    }

    /* My Schedule empty state — "create your own events" intro (left-aligned) */
    .block-sessions-agenda .myschedule-intro {
        margin-top: -23px;
        margin-bottom: 2.5rem;
        text-align: left;
    }

    /* Day-scoped empty shows the filter bar, so the intro sits below it normally
       — cancel the pull-up (higher specificity wins in every breakpoint). */
    .block-sessions-agenda .myschedule-intro.has-filters {
        margin-top: 0;
    }

    .block-sessions-agenda .myschedule-intro p {
        font-family: var(--font-hubspot-sans);
        font-weight: 300;
        font-size: 14px;
        line-height: 1.5;
        letter-spacing: 0.28px;
        color: var(--magenta);
        margin-bottom: 23px; /* Figma: 23px between the copy and Create My Event */
        max-width: 555px; /* Figma: 555px */
    }

    /* Shared pill CTAs used across the My Schedule empty states */
    .block-sessions-agenda .ms-cta-outline,
    .block-sessions-agenda .ms-cta-filled {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: fit-content;
        border-radius: 50px;
        border: 1px solid var(--magenta);
        padding: 10px 20px;
        font-family: var(--font-hubspot-serif);
        font-size: calc(17 / var(--base-fs) * 1rem);
        line-height: 1.4;
        letter-spacing: 0.34px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
    }

    .block-sessions-agenda .ms-cta-outline {
        background: transparent;
        color: var(--magenta);
    }

    .block-sessions-agenda .ms-cta-filled {
        background: var(--magenta);
        color: #fff;
    }

    /* Build My Schedule uses the darker-pink fill from Figma, not magenta. */
    .block-sessions-agenda .ms-cta-build {
        background: #7d4566;
        border-color: #7d4566;
    }

    @media (hover: hover) {
        .block-sessions-agenda .ms-cta-outline:hover {
            background: var(--magenta);
            color: #fff;
        }

        .block-sessions-agenda .ms-cta-filled:hover {
            background: var(--purple);
            border-color: var(--purple);
        }

        .block-sessions-agenda .ms-cta-build:hover {
            background: var(--magenta);
            border-color: var(--magenta);
        }
    }
</style>
